import {
    NetworkStatus,
    useLazyQuery,
    useMutation,
    useQuery,
    useReactiveVar,
} from '@apollo/client'
import { UpdateEditingTimeInput } from '@deltek/specification-client'
import { AuthContext } from '@deltek/specification-client/core/Configurations/AuthContext'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { AppBarContext } from '@deltek/specification-client/core/Contexts/AppBarContext'
import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import { GetProjectManufacturerProductListingInput } from '@deltek/specification-client/core/Models/projectManufacturerProductListing'
import { ProjectUniformatClassification } from '@deltek/specification-client/core/Models/projectUniformatClassification'
import { UniformatClassification } from '@deltek/specification-client/core/Models/uniformatClassification'
import { UpdatePropertiesForPUCInput } from '@deltek/specification-client/core/Models/uniformatClassificationProperty'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import usePrevious from '@deltek/specification-client/core/Utilities/Hooks/usePrevious'
import { updateKpMainLocalStore } from '@deltek/specification-client/core/Utilities/localstorageUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { ListSubheader, MenuItem, Select } from '@material-ui/core'
import Backdrop from '@material-ui/core/Backdrop'
import Box from '@material-ui/core/Box'
import CircularProgress from '@material-ui/core/CircularProgress'
import { Theme, makeStyles } from '@material-ui/core/styles'
import LoyaltyOutlinedIcon from '@material-ui/icons/LoyaltyOutlined'
import SearchIcon from '@material-ui/icons/Search'
import SettingsIcon from '@material-ui/icons/Settings'
import Alert from '@material-ui/lab/Alert'
import AlertTitle from '@material-ui/lab/AlertTitle'
import { UpdateProjectSettings } from 'api/graphQL/mutations/project.mutations'
import {
    UpdateEditingTime,
    UpdatePropertyValuesForUniformatClassificationMutation,
} from 'api/graphQL/mutations/projectElement.mutations'
import { CategoriesQuery } from 'api/graphQL/queries/category.queries'
import {
    GetElementNodesForOfficeMasterWorkspaceQuery,
    GetFamilyNodesBySustainabilityTopicQuery,
    GetProjectElementNodesForAEProjectWSQuery,
    GetProjectElementNodesQuery,
    GetProjectElementNodesWithMetadataForAEProjectWSQuery,
    GetProjectManufacturerProductListingQuery,
    GetProjectUniformatClassification,
    GetProjectUniformatClassificationElement,
    GetUniformatClassification,
} from 'api/graphQL/queries/projectElements.queries'
import { GetProjectElementsTotalCountQuery } from 'api/graphQL/queries/projectElementsTotalCount.queries'
import {
    ElementType,
    setElementNotesCount,
    setProjectElementMetadata,
    setWorkspaceSelectedElement,
} from 'api/graphQL/reactiveVars/element'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import {
    contentTeamProjectId,
    isContentTeam,
    isOmWorkspaceUser,
    reactiveWorkspaceContents,
    setIsReactiveContentTeamFromStudioPage,
    setIsRemoveNuggetGqlOngoing,
    setWorkspaceContentItems,
} from 'api/graphQL/reactiveVars/workspaceContent'
import { setIsTrackChangesToggleOn } from 'api/graphQL/reactiveVars/workspaceShowHideToggles'
import {
    ProjectBulkAddResult,
    StartProjectBulkAddInput,
} from 'api/graphQL/types/projectBulk.types'
import {
    GetFamilyNodesBySustainabilityTopicResponse,
    GetProjectElementNodesResponse,
    GetProjectManufacturerProductListingResponse,
    GetProjectNuggetResponse,
    GetProjectUniformatClassificationElementResponse,
    GetProjectUniformatClassificationResponse,
    GetUniformatClassificationResponse,
    ProjectElementNodeWithMetadata,
    UpdatePUCPropertyValuesResponse,
} from 'api/graphQL/types/projectElement.types'
import { GetProjectElementsTotalCountResponse } from 'api/graphQL/types/projectElementsTotalCount.type'
import clsx from 'clsx'
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog'
import CategoryPane from 'components/ElementsTree/components/CategoryPanel/CategoriesPane'
import CollapseIcon from 'components/ElementsTree/components/CategoryPanel/CollapseIcon'
import { LeftMostPane } from 'components/ElementsTree/components/LeftMostPane/LeftMostPane'

import { DataPrivacySettings } from '@deltek/specification-client/core/Models/dataPrivacySettings'
import { FirmDataPrivacySettingsQuery } from 'api/graphQL/queries/firm.queries'
import { useElementTreeStore } from 'components/ElementsTree/ElementsTreeStore'
import {
    useBulkAddBarAction,
    useBulkAddBarShallowSelector,
    useBulkAddBarStore,
} from 'components/ElementsTree/components/BulkAddBar2/BulkAddBarStore'
import SearchBarLeftMostPane from 'components/ElementsTree/components/LeftMostPane/SearchBarLeftMostPane'
import Workspace2ActionBar from 'components/Workspace2ActionBar/Workspace2ActionBar'
import { useElementItemShallowSelector } from 'components/WorkspaceEditor/ElementItemStore'
import {
    useProjectSettings,
    useWorkspace2Actions,
} from 'components/WorkspaceEditor/Workspace2Store'
import { usePTCategoryStateUpdate } from 'components/WorkspaceEditor/hooks'
import { useElementResetAfterBulkOperation } from 'components/WorkspaceEditor/hooks/useElementResetAfterBulkOperation'
import { NotificationProvider } from 'components/WorkspaceEditor/services'
import { processEnv } from 'components/configuration/environmentVariables'
import { ProductFamilyStudioManagement } from 'models/StudioManagement/studioManagement'
import {
    CategoryPanel,
    CategoryPanelResponse,
    ProfessionalDisciplineInner,
} from 'models/category'
import {
    GetFamilyNodesBySustainabilityTopicInput,
    GetOMProjectElementNodesInput,
    GetProjectElementNodesInput,
    NodeSearchFilter,
} from 'models/getProjectElementNodesInput'
import { GetProjectElementNodeCountInput } from 'models/getProjectElementsTotalCount'
import {
    GetOMProjectElementNodesResponseForWorkspace,
    ProjectElementWithProjectNode,
} from 'models/projectElementNode'
import {
    ElementPanelFilterItem,
    ElementPanelSubItem,
    ProjectElementMetadata,
} from 'models/projectElements'
import {
    ProjectSettings,
    ProjectSettingsInput,
    UpdateProjectSettingsResponse,
} from 'models/projectSettings'
import React, { useContext, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import AddToProjectIcon from 'resources/AddToProjectIcon'
import TwoColumnIcon from 'resources/TwoColumnIcon'
import { usePreviewFeatures } from 'shared/hooks/usePreviewFeatures'
import { useBetaWorkspaceFlag } from 'shared/useBetaWorkspaceFlag'
import {
    getNestedObjectPropertyValue,
    setNestedObjectPropertyValue,
} from 'shared/utils/objectUtils'
import {
    RemovedElementResult,
    WORKSPACE_CATEGORY_CHANGE_KEY,
    WORKSPACE_PRODUCT_LISTING_ACTION,
    WORKSPACE_PRODUCT_LISTING_KEY,
    WORKSPACE_TREE_ELEMENT_NODES,
    WorkspaceProductListing,
    WorkspaceTreeElementNodes,
    checkProductFamilyNameChanged,
    getAssemblyPathByElement,
    getDialogUrl,
    getDivisionNumberOfSelectedDivision,
    getElementIndexInTreePath,
    getFromLocalStorage,
    getLocalStorageValue,
    getNodeByItemIdentifier,
    getSortedList,
    getUpdateBpElementListByTreePath,
    insertProductTypeElement,
    isElementHasChildrenById,
    localStorageItemKey,
    localStorageKey,
    propagateIsInProjectToAncestors,
    removeElementNodeFromList,
    replaceElementsList,
    sortProjectElementsNodes,
    updatedDivisionElements,
} from 'shared/utils/workspaceUtils'
import { ProjectPageContext } from '../ProjectPage/components/ProjectPageContext'
import BulkAddBar from './components/BulkAddBar/BulkAddBar'
import BulkAddSuccessDialogContent from './components/BulkAddSuccessDialogContent'
import CategoriesPanel from './components/CategoriesPanel'
import { convertToBulkAddElementInputs } from './components/Common/addUtils'
import { ElementItemIdentifier } from './components/ElementsPanel/ElementTreeView'
import ElementsPanel from './components/ElementsPanel/ElementsPanel'
import ProjectBulkAddProgressDialog from './components/ProjectBulkAddProgressDialog'
import { handleViewNotes } from './components/ProjectNotesDialog/notesUtils'
import ProjectParametersDialog from './components/ProjectParametersDialog'
import ProjectSettingsDialog from './components/ProjectSettingsDialog'
import SearchBar from './components/SearchBar/SearchBar'
import ShowHideBar, { ShowHideBarItem } from './components/ShowHideBar'
import SustainabilityFamilyTreeList from './components/SustainabilityFamilyTreeList'
import WorkspaceActionBar, {
    WorkspaceActionBarWidget,
} from './components/WorkspaceActionBar'
import WorkspaceActionBarButtonWidget from './components/WorkspaceActionBarButtonWidget'
import {
    WorkspaceActionBarContext,
    WorkspaceActionBarProvider,
} from './components/WorkspaceActionBarContext'
import WorkspaceActionBarIconButtonWidget from './components/WorkspaceActionBarIconButtonWidget'
import { default as WorkspaceActionBarIconTextWidget } from './components/WorkspaceActionBarIconTextWidget'
import WorkspaceContentArea from './components/WorkspaceContentArea'
import { WorkspaceContentContext } from './components/WorkspaceContentContext'
import { RightMostPaneProvider } from './components/WorkspaceRightPane/context/RightMostPaneContext'
import WorkspaceSettingsDialog from './components/WorkspaceSettingsDialog'
import {
    ADDED_TO_DRAFT,
    ADDED_TO_PROJECT,
    ALL_ELEMENTS_TEXT,
    ALL_PROJECTS_ELEMENTS_TEXT,
    PERSISTED_ELEMENT_ASSEMBLIES,
    PERSISTED_ELEMENT_DIVISIONS,
    PREVIOUS_ADDEDTOPROJECT_PARENT_ASSEMBLIES,
    PREVIOUS_ADDEDTOPROJECT_PARENT_DIVISIONS,
} from './constants'

export const MaxElementsPerBatch = 1000

// Timeout threshold for OM Workspace queries (30 seconds)
const OM_QUERY_TIMEOUT_MS = 30000

/**
 * Client-side chunking for large OM families
 *
 * Why chunk already-fetched data?
 * - Large families (500+ elements) cause UI freeze during state updates
 * - React re-renders the entire tree when state changes
 * - Processing 500+ elements at once blocks the event loop
 * - Chunking yields control back to browser between batches
 * - Keeps UI responsive during large state updates
 * - Each chunk takes ~10-20ms, but allows rendering/user input between chunks
 *
 * Why not server-side pagination?
 * - OM Workspace requires all elements for drag-drop reordering
 * - Full tree needed for expand/collapse functionality
 * - Virtual scrolling already implemented but doesn't solve state update freeze
 */
const OM_CHUNK_SIZE = 200

const processDataInChunks = async <T,>(
    data: T[],
    chunkSize: number,
    onChunkProcessed: (chunk: T[], isLastChunk: boolean) => void
): Promise<void> => {
    return new Promise((resolve) => {
        let index = 0

        const processChunk = () => {
            const chunk = data.slice(index, index + chunkSize)
            const isLastChunk = index + chunkSize >= data.length

            // Process current chunk
            onChunkProcessed(chunk, isLastChunk)

            index += chunkSize

            if (index < data.length) {
                // Use setTimeout to yield to browser and keep UI responsive
                setTimeout(processChunk, 0)
            } else {
                resolve()
            }
        }

        processChunk()
    })
}

export interface CategoryElementListStateInfo {
    data: ProjectElementNode[] | ProjectElementNodeWithMetadata[]
    elementSkipCount: number
    hasMoreElementsToFetch: boolean
    totalElementCount: number
    isLoading: boolean
}

export interface KeyValuePair {
    elementId?: string
    elementValue?: string
}

interface GetMoreElementListInputType {
    forceReset: boolean
    parentNodeId: string | undefined
    elementId: string | undefined
    /**
     * Set to true if the request is coming from a bulk select action.
     * The children of {@link parentNodeId} will be loaded and then the bulk select action will be triggered,
     * adding the children to the bulk-add list.
     */
    isBulkSelectRequested: Boolean
}

export interface WorkspacePageStyleProps {
    isSearching?: boolean
    isShowHideBarVisible?: boolean
    isBulkAddBarVisible?: boolean
    isBetaWorkspaceToggleEnabled?: boolean
}
const useWorkspaceStyles = makeStyles((theme: Theme) => ({
    panel: {
        overflowX: 'hidden',
        transition: '100ms',
        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
        zIndex: 1,
        '& *::-webkit-scrollbar': {
            width: 6,
            height: 20,
        },
        '& *::-webkit-scrollbar-thumb': {
            backgroundColor: '#464646',
            borderRadius: 100,
        },
        '& *::-webkit-scrollbar-track': {
            boxShadow: 'none',
        },
    },
    rightPane: {
        marginTop: pxToRem(5),
        marginBottom: pxToRem(5),
        width: pxToRem(376),
    },
    collapsedPane: {
        width: 0,
        padding: theme.spacing(0),
    },
    collapsedRightPane: {
        width: 36,
        padding: theme.spacing(0),
    },
    expandedPane: {
        width: 349,
        padding: theme.spacing(1),
        minWidth: 250,
    },

    expandedCategoriesPane: {
        width: 342,
        padding: theme.spacing(1),
        '@media (max-width: 1460px)': {
            width: 320,
        },
        '@media (max-width: 1366px)': {
            width: 300,
        },
        '@media (max-width: 1200px)': {
            width: 260,
        },
    },
    fullWidthPaneContentContainer: {
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        position: 'relative',
    },
    fullWidthPaneCategoryContainer: {
        width: '100%',
        paddingRight: theme.spacing(1),
        height: '100%',
        overflow: 'auto',
        '&:focus': {
            backgroundColor: theme.palette.common.white,
        },
        '&::-webkit-scrollbar': {
            width: 6,
            height: 6,
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#464646',
            borderRadius: 100,
        },
        '&::-webkit-scrollbar-track': {
            boxShadow: 'none',
            marginTop: theme.spacing(2.5),
        },
    },
    panelTitleWithGroupping: {
        textTransform: 'uppercase',
        marginRight: theme.spacing(4),
    },
    panelTitleContainerGroupValue: { fontWeight: 'normal' },
    panelTitleContainerGroupType: { fontWeight: 600 },
    categoriesPane: {
        backgroundColor: '#EDF2F2',
    },

    elementsPane: {
        backgroundColor: '#F5F5F5',
    },
    mainContentPane: {
        backgroundColor: theme.palette.common.white,
        paddingRight: '0 !important',
    },
    mainContentLarge: {
        backgroundColor: '#efefef',
        '& .header': {
            flexWrap: 'nowrap',
        },
        '& .tabContainer': {
            flexFlow: 'row-reverse',
        },
        '& .elementTitleContainer': {
            flexWrap: 'nowrap',
        },
    },
    container: {
        position: 'absolute',
        top: ({
            isSearching,
            isBulkAddBarVisible,
            isShowHideBarVisible,
            isBetaWorkspaceToggleEnabled,
        }: WorkspacePageStyleProps) =>
            0 +
            (isShowHideBarVisible
                ? isBetaWorkspaceToggleEnabled
                    ? 60
                    : 46
                : 0) +
            (isSearching ? (isBetaWorkspaceToggleEnabled ? 0 : 70) : 0) +
            (isBulkAddBarVisible
                ? isBetaWorkspaceToggleEnabled
                    ? 60
                    : 70
                : 0),
        bottom: 0,
        maxWidth: 1920,
        flexWrap: 'nowrap',
        [theme.breakpoints.down(theme.breakpoints.values.xl + 1)]: {
            left: 0,
        },
        overflow: 'hidden',
    },
    showHideContainer: {
        backgroundColor: '#C27A40',
        height: 40,
        position: 'absolute',
        left: 0,
        top: 0,
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: theme.palette.common.white,
    },
    padded: {
        padding: theme.spacing(2),
        paddingTop: theme.spacing(0),
        paddingBottom: theme.spacing(0),
    },
    searchBarContainer: {
        background: '#F2F2F2',
        height: pxToRem(70),
        position: 'absolute',
        top: ({ isShowHideBarVisible }: WorkspacePageStyleProps) =>
            isShowHideBarVisible ? 45 : 0,
        borderBottom: '1px solid #C9C9C9',
        left: 0,
    },
    bulkAddContainer: {
        background: '#F2F2F2',
        height: ({ isBetaWorkspaceToggleEnabled }: WorkspacePageStyleProps) =>
            isBetaWorkspaceToggleEnabled ? pxToRem(57) : pxToRem(70),
        position: 'absolute',
        top: ({
            isShowHideBarVisible,
            isSearching,
            isBetaWorkspaceToggleEnabled,
        }: WorkspacePageStyleProps) =>
            isShowHideBarVisible
                ? isSearching
                    ? 115
                    : isBetaWorkspaceToggleEnabled
                    ? 55
                    : 45
                : isSearching
                ? 70
                : 0,
        borderBottom: '1px solid #C9C9C9',
        left: 0,
    },
    customWidgetContainer: {
        display: 'flex',
        flexDirection: 'column',
        padding: theme.spacing(0, 1.25),
        marginTop: theme.spacing(0.25),
        transition: '100ms',
        minWidth: pxToRem(60),
    },
    inputSelectText: {
        fontSize: 14,
        position: 'absolute',
        textTransform: 'uppercase',
        padding: theme.spacing(0.5, 0, 0, 1),
        margin: theme.spacing(1, 0, 1.5, 0),
        width: '100%',
        color: '#185E8E',
        fontWeight: 500,
        '& .MuiSvgIcon-root': {
            color: '#185E8E',
            marginRight: theme.spacing(2),
        },
        '& .MuiInputBase-input': {
            paddingBottom: theme.spacing(0.4),
        },
        '& .MuiSelect-select:focus': {
            backgroundColor: 'transparent',
        },
        '& .MuiMenu-paper': {
            top: 240,
        },
    },
    collapseIcon: {
        color: '#000000 54%',
        cursor: 'pointer',
    },
    omTimeoutAlert: {
        position: 'fixed',
        top: 80,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        width: '90%',
        maxWidth: 800,
    },
}))

const AllCategoriesObject = { id: 'all', name: 'All Categories' }

export interface WorkspacePageProps {
    persistState?: (propKey: string, inputValue: boolean | any) => void
    userId: string
    onStateChange?: (stateKey: string, newValue: any, persist?: boolean) => void
    userRoles?: string[]
    contentTeamFromStudioPage?: boolean
    isDraftMode?: boolean
    setIsDraftMode?: (val: boolean) => void
}

type state = {
    projectElementNodeId: string
    isDivisionViewNode: boolean
    fromStudio: boolean
}

export const WorkspacePage: React.FC<WorkspacePageProps> = ({
    persistState,
    userId,
    onStateChange,
    userRoles,
    contentTeamFromStudioPage,
    isDraftMode,
    setIsDraftMode,
}) => {
    const history = useHistory()
    const location = useLocation()
    const { currentUserInfo } = useContext(AuthContext)

    const {
        elementItemValue,
        setElementItemValue,
        setSelectedElementFormat,
        setSelectedElementFilterValue,
    } = useElementItemShallowSelector((state) => ({
        elementItemValue: state.elementItemValue,
        setElementItemValue: state.setElementItemValue,
        setSelectedElementFormat: state.setSelectedElementFormat,
        setSelectedElementFilterValue: state.setSelectedElementFilterValue,
    }))
    const {
        bulkAddBaseElementIds,
        setBulkAddBaseElementIds,
    } = useBulkAddBarShallowSelector((state) => ({
        bulkAddBaseElementIds: state.bulkAddBaseElementIds,
        setBulkAddBaseElementIds: state.actions.setBulkAddBaseElementIds,
    }))
    const setUpdateElementPanelState = useElementTreeStore(
        (store) => store.setUpdateElementPanelState
    )
    const currentUserId = React.useMemo(() => currentUserInfo?.kpUserId, [
        currentUserInfo,
    ])

    const { projectId, elementId } = useParams<{
        projectId: string
        elementId?: string
    }>()

    const selectedElementIdFromDesignProgressTableInitialValue = elementId
    const { projectElementNodeId, fromStudio, isDivisionViewNode } =
        (history.location.state as state) || {}
    const [
        selectedElementIdFromDesignProgressTable,
        setSelectedElementIdFromDesignProgressTable,
    ] = React.useState(selectedElementIdFromDesignProgressTableInitialValue)

    const [fromStudioValue, setFromStudiovalue] = React.useState(fromStudio)
    const [startTime, setStartTime] = React.useState<number | null>(
        new Date().getTime()
    )

    /**
     * OM Workspace timeout handling for large families (PBI 2503726, Bug 2515739)
     *
     * Purpose: Large OM families (500+ elements) can take 30+ seconds to load.
     * Without feedback, users may think the app froze. This mechanism:
     * 1. Tracks loading time with a counter (omQueryTimeoutSeconds)
     * 2. Shows an alert after 30 seconds with elapsed time
     * 3. Suggests using Beta Workspace for better performance
     *
     * Timer cleanup handled in:
     * - useEffect cleanup function (component unmount or dependency change)
     * - Alert dismiss handler (user closes the alert)
     * - Loading completion (elementsListLoading becomes false)
     */
    const [omQueryTimeout, setOmQueryTimeout] = React.useState(false)
    const [omQueryTimeoutSeconds, setOmQueryTimeoutSeconds] = React.useState(0)
    const omTimeoutTimersRef = React.useRef<{
        timeout: NodeJS.Timeout | null
        interval: NodeJS.Timeout | null
    }>({
        timeout: null,
        interval: null,
    })

    // OM Workspace chunking state for large datasets
    const [isProcessingChunks, setIsProcessingChunks] = React.useState(false)

    const { isSystemUser, isAdmin, isPublishingAuthor } = useReactiveVar(
        userInfo
    )

    let productDialogWindow: any = null

    // checking if the user is tracked on workspace.
    // Only SMM users (Admin, publishing author) are tracked
    const isUserTrackable = React.useMemo(() => {
        return (
            contentTeamFromStudioPage &&
            isSystemUser &&
            (isAdmin || isPublishingAuthor)
        )
    }, [isSystemUser, contentTeamFromStudioPage, isAdmin, isPublishingAuthor])

    const [
        isDivisionViewNodeValue,
        setisDivisionViewNodeValue,
    ] = React.useState(isDivisionViewNode)
    const { bulkAddErrorMessage, bulkAddResponse } = useBulkAddBarStore()
    const {
        setAddBulkItemsInProgress,
        setBulkAddResponse,
        setBulkAddErrorMessage,
    } = useBulkAddBarAction()
    const [
        sustanaibilityTopicFamilies,
        setSustanaibilityTopicFamilies,
    ] = React.useState<ProjectElementNode[] | undefined>([])

    const [
        projectProductListingCount,
        setProjectProductListingCount,
    ] = React.useState<Number | undefined>()

    const [isProjectSettingsOpen, setIsProjectSettingsOpen] = React.useState<
        boolean
    >(false)
    const [targetElementFound, setTargetElementFound] = React.useState(false)
    const [
        addingBulkItemsInProgress,
        setAddingBulkItemsInProgress,
    ] = React.useState(false)
    const [cancelBulkItems, setCancelBulkItems] = React.useState(false)
    const [
        isGetProjectElementNodesError,
        setIsGetProjectElementNodesError,
    ] = React.useState(false)
    const [addToProjectSuccess, setAddToProjectSuccess] = React.useState(false)
    const [addedBulkAddItemsCount, setAddedBulkAddItemsCount] = React.useState(
        0
    )
    const [
        bulkAddSkippedElementPaths,
        setBulkAddSkippedElementPaths,
    ] = React.useState<string[]>([])

    const isOMUser = useReactiveVar(isOmWorkspaceUser)
    const { previewFeatures } = usePreviewFeatures()

    // to check if the user is on AE project workspace
    const isAEUserProjectWorkspace = React.useMemo(
        () =>
            !contentTeamFromStudioPage &&
            processEnv.REACT_APP_EnableVersionControl === '1' &&
            isOMUser,
        [contentTeamFromStudioPage, isOMUser, processEnv]
    )

    /**
     * Update changes in the bug fix: #2195159
     * When user click on the design progress table on the project overview page, the isFromRedirect flag is set to true.
     * This flag is used to indicate that the user was redirected from the linked element on that page.
     * However, there are some issues with the current implementation. The flag does not wait for the new data to load, leading to the component rendering
     * with outdated data. Additionally, the nugget data was not loading at all.
     * To address this, Updated the logic to check the isFromRedirect flag once the new data has been successfully loaded.
     * The flag is then set to false, ensuring that the component renders with the latest data.
     */
    const isFromRedirect = React.useMemo(
        () =>
            !!selectedElementIdFromDesignProgressTableInitialValue &&
            !targetElementFound &&
            !isGetProjectElementNodesError,
        [
            selectedElementIdFromDesignProgressTableInitialValue,
            targetElementFound,
            isGetProjectElementNodesError,
        ]
    )

    const [_isMounted, _setIsMounted] = React.useState(false)

    const previousIsRedirect = usePrevious(isFromRedirect)
    // isContentTeamUser is used to disable persistence for Deltek System Accounts
    const isContentTeamUser = useReactiveVar(isContentTeam)
    // hook to check if the beta workspace is enabled using url params
    const { isBetaWorkspaceEnabled } = useBetaWorkspaceFlag(location.search)

    const showHideBarItems: ShowHideBarItem[] = [
        ...(!contentTeamFromStudioPage
            ? [
                  {
                      label: 'Categories',
                      stateKey: 'showHideStates.categories',
                  },
              ]
            : []),

        {
            label: contentTeamFromStudioPage
                ? 'MasterSpec Elements'
                : 'Project Elements',
            stateKey: 'showHideStates.elements',
        },
        {
            label: 'Action Bar',
            stateKey: 'showHideStates.actionBar',
        },
        ...((!contentTeamFromStudioPage || previewFeatures.hasOMWorkspace) &&
        (previewFeatures.hasNewWorkspace2 || isBetaWorkspaceEnabled)
            ? [
                  {
                      label: contentTeamFromStudioPage
                          ? 'Switch to New OM Workspace'
                          : 'Switch to New Workspace',
                      stateKey: 'showHideStates.betaWorkspace',
                  },
              ]
            : []),
    ]

    const serviceWorker = navigator.serviceWorker
    const {
        setCustomActionBar,
        updateBrowserTitle,
        currentUserActiveFirm,
        setActionBarVisibility,
    } = React.useContext(AppBarContext)
    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const { data: firmData } = useQuery<{
        firm: { id: string; dataPrivacySettings: DataPrivacySettings }
    }>(FirmDataPrivacySettingsQuery, {
        variables: { firmId: currentUserActiveFirm?.firmId },
        skip: !currentUserActiveFirm?.firmId,
    })

    const [allDisciplineData, setData] = React.useState<
        ProfessionalDisciplineInner[]
    >([])

    const getPersistedState = React.useCallback(
        (propKey: string) => {
            return getFromLocalStorage(currentUserId, projectId!, propKey)
        },
        [currentUserId, projectId]
    )
    const [selectedTopic, setSelectedTopic] = React.useState<
        Partial<ProductFamilyStudioManagement> | undefined
    >(getPersistedState?.('selectedTopicData') ?? undefined)

    const handleUpdateSelectedTopic = React.useCallback(
        (topic?: Partial<ProductFamilyStudioManagement>) => {
            setSelectedTopic(topic)
            persistState?.('selectedTopicData', topic)
        },
        [setSelectedTopic, persistState]
    )
    const [isSearchInitiated, setIsSearchInitiated] = React.useState<
        boolean | undefined
    >(getPersistedState?.('searchBarDisplayed'))

    const [isToggleBarVisible, setIsToggleBarVisible] = React.useState<
        boolean | undefined
    >(getPersistedState?.('showHideBarDisplay') ?? true)

    const [searchFilters, setSearchFilters] = React.useState<
        NodeSearchFilter[] | undefined
    >(!isFromRedirect ? getPersistedState?.('searchFilters') : undefined)
    const prevSearchFilters = usePrevious(searchFilters)
    /**
     * Active when the user submitted a whitespace/dash-only query. The backend
     * rejects empty search text, so we render the "No sections match your
     * search" empty state without firing the query. Not persisted — whitespace
     * shouldn't survive a refresh.
     */
    const [isWhitespaceSearch, setIsWhitespaceSearch] = React.useState<boolean>(
        false
    )
    const [skipDefaultQuery, setSkipDefaultQuery] = React.useState<boolean>(
        false
    )
    const setIsSearchBarDisplayed = useElementTreeStore(
        (store) => store.setIsSearchBarDisplayed
    )
    const {
        categoryElementStates,
        setCategoryElementStates,
        selectedElementIdentifier,
        setSelectedElementIdentifier,
        treeIndexerRef,
        projectData,
        projectLoading,
        selectedBulkAddElements,
        setSelectedBulkAddElements,
        refreshProjectElementNodes,
        handleRefreshProjectElementNodes,
    } = React.useContext(ProjectPageContext)
    const [isBulkAddVisible, setIsBulkAddVisible] = React.useState<
        boolean | undefined
    >(selectedBulkAddElements && selectedBulkAddElements.length > 0)

    const [
        parentNodeIdPendingBulkSelect,
        setParentNodeIdPendingBulkSelect,
    ] = React.useState<string>('')

    const [isBulkAddDataLoading, setIsBulkAddDataLoading] = React.useState<
        boolean
    >(false)
    const { currentState, setStateValue } = React.useContext(
        WorkspaceActionBarContext
    )
    const isBetaWorkspaceToggleEnabled =
        (!contentTeamFromStudioPage || previewFeatures.hasOMWorkspace) &&
        (previewFeatures.hasNewWorkspace2 || isBetaWorkspaceEnabled) &&
        currentState?.showHideStates?.betaWorkspace
    const classes = useWorkspaceStyles({
        isSearching: isSearchInitiated,
        isShowHideBarVisible: isToggleBarVisible,
        isBulkAddBarVisible: isBulkAddVisible,
        isBetaWorkspaceToggleEnabled,
    })

    const bulkAddInput: StartProjectBulkAddInput = React.useMemo(
        () => ({
            elements: convertToBulkAddElementInputs(
                selectedBulkAddElements ?? []
            ),
            projectId,
        }),
        [selectedBulkAddElements, projectId]
    )
    const initiallySelectedCategory = React.useMemo(
        () => getPersistedState?.('selectedCategory'),
        [getPersistedState]
    )

    const projectSettings = useProjectSettings()
    const { setProjectSettings } = useWorkspace2Actions()
    const resetElementAfterBulkOperation = useElementResetAfterBulkOperation()

    const initialElementFilterType: ElementPanelSubItem = React.useMemo(() => {
        return (
            //TODO: check priority of the value in persistence and the value from the project settings
            getPersistedState?.('elementPanelFilterType') ?? {
                type: isBetaWorkspaceToggleEnabled
                    ? projectSettings?.elementTreeView?.toString() ??
                      'DIVISIONS'
                    : 'ASSEMBLIES',
                value: contentTeamFromStudioPage
                    ? ALL_ELEMENTS_TEXT
                    : ALL_PROJECTS_ELEMENTS_TEXT,
            }
        )
    }, [getPersistedState, projectSettings, isBetaWorkspaceToggleEnabled])

    const updatedFilteroptions: ElementPanelFilterItem[] = [
        {
            type: 'ASSEMBLIES',
            options: [
                {
                    type: 'ASSEMBLIES',
                    value: contentTeamFromStudioPage
                        ? ALL_ELEMENTS_TEXT
                        : ALL_PROJECTS_ELEMENTS_TEXT,
                },
                {
                    type: 'ASSEMBLIES',
                    value: contentTeamFromStudioPage
                        ? ADDED_TO_DRAFT
                        : ADDED_TO_PROJECT,
                },
                { type: 'ASSEMBLIES', value: 'Added to Work Results' },
            ],
        },
        {
            type: 'DIVISIONS',
            options: [
                {
                    type: 'DIVISIONS',
                    value: contentTeamFromStudioPage
                        ? ALL_ELEMENTS_TEXT
                        : ALL_PROJECTS_ELEMENTS_TEXT,
                },
                {
                    type: 'DIVISIONS',
                    value: contentTeamFromStudioPage
                        ? ADDED_TO_DRAFT
                        : ADDED_TO_PROJECT,
                },
            ],
        },
    ]

    // remove the env condition when OM Version is production ready
    const isOMWorkspace = React.useMemo(
        () =>
            processEnv.REACT_APP_EnableVersionControl === '1' &&
            isOMUser &&
            projectId === currentUserActiveFirm?.bestPracticeProjectId,
        [projectId, isOMUser, currentUserActiveFirm, processEnv]
    )
    const [fetchAllElements, setFetchAllElements] = React.useState<boolean>(
        false
    )
    const [
        selectedElementFilterType,
        setSelectedElementFilterType,
    ] = React.useState<ElementPanelSubItem>(initialElementFilterType)
    const [selectedCategory, setSelectedCategory] = React.useState<
        CategoryPanel | undefined | null
    >(
        isFromRedirect
            ? AllCategoriesObject
            : initiallySelectedCategory ?? AllCategoriesObject
    )
    const previewElementFilterType = usePrevious(
        selectedElementFilterType ?? initialElementFilterType
    )
    const initiallySelectedDiscipline = React.useMemo(
        () => getPersistedState?.('selectedDiscipline'),
        [getPersistedState]
    )
    const [selectedDiscipline, setSelectedDiscipline] = React.useState<
        ProfessionalDisciplineInner | undefined | null
    >(initiallySelectedDiscipline)

    const [
        showElementsPanelFullLoadState,
        setShowElementsPanelFullLoadState,
    ] = React.useState(false)
    const [autoCategorySelection, setAutoaCategorySelection] = React.useState<
        boolean
    >(false)
    const [isFetchingMoreElements, setIsFetchingMoreElements] = React.useState(
        false
    )
    const [isNewDataLoading, setIsNewDataLoading] = React.useState<boolean>(
        false
    )
    const [
        selectedProjectElementNode,
        setSelectedProjectElementNode,
    ] = React.useState<ProjectElementNodeWithMetadata | null>(null)

    const [selectedElementData, setSelectedElementData] = React.useState<
        | Partial<ProjectUniformatClassification>
        | Partial<UniformatClassification>
        | undefined
    >(undefined)

    const initialCategroyPaneVisibleValue = React.useMemo(() => {
        const value = getFromLocalStorage(
            userId,
            projectId!,
            `${selectedProjectElementNode?.id}-${selectedProjectElementNode?.baseElementId}-isCategoryPaneVisible`
        )
        return value
    }, [userId, selectedProjectElementNode, projectId])

    const initialElementPaneVisibleValue = React.useMemo(() => {
        const value = getFromLocalStorage(
            userId,
            projectId!,
            `${selectedProjectElementNode?.id}-${selectedProjectElementNode?.baseElementId}-isElementPaneVisible`
        )
        return value
    }, [userId, selectedProjectElementNode, projectId])

    const [isElementPaneVisible, setIsElementPaneVisible] = React.useState<
        boolean
    >(initialElementPaneVisibleValue)
    const [isCategoryPaneVisible, setIsCategoryPaneVisible] = React.useState<
        boolean
    >(initialCategroyPaneVisibleValue)
    const { isRemoveNuggetGqlOngoing } = reactiveWorkspaceContents()

    // Watch for PT activation updates and update selectedProjectElementNode + categoryElementStates
    usePTCategoryStateUpdate({
        categoryElementStates,
        setCategoryElementStates,
        selectedProjectElementNode,
        setSelectedProjectElementNode,
        selectedCategory,
    })

    const elementStates:
        | CategoryElementListStateInfo
        | undefined = React.useMemo(() => {
        return selectedCategory?.name !== 'Work Results' &&
            selectedElementFilterType?.value === 'Added to Work Results'
            ? undefined
            : categoryElementStates?.[selectedCategory?.id ?? 'all'] ?? {}
    }, [categoryElementStates, selectedCategory, selectedElementFilterType])
    const handleChangeFilterOption = React.useCallback(
        (
            selectedMenuItem?: ElementPanelSubItem,
            event?: React.ChangeEvent<{
                name?: string | undefined
                value: unknown
            }>
        ) => {
            event?.stopPropagation()

            if (
                (selectedElementFilterType?.value === selectedMenuItem?.value &&
                    selectedElementFilterType?.type ===
                        selectedMenuItem?.type) ||
                !selectedMenuItem
            )
                return
            let categoyPanelDiv = document.getElementById(
                'categoryPanel'
            ) as HTMLElement
            if (
                selectedElementFilterType?.type === 'ASSEMBLIES' &&
                selectedMenuItem?.type === 'DIVISIONS' &&
                selectedProjectElementNode?.elementType === 'ASSEMBLY'
            ) {
                setSelectedElementData(undefined)
            }

            setSelectedElementFilterType(selectedMenuItem!)
            setSelectedElementFormat?.(selectedMenuItem?.type ?? '')
            setFetchAllElements(true)
            setStateValue('elementPanelFilterType', selectedMenuItem, true)
            if (selectedMenuItem?.value === 'Added to Work Results') {
                setAutoaCategorySelection(true)
                handleSelectedCatgoryChange(
                    {
                        name: 'Work Results',
                        id: '1f689538-1896-41a1-b66b-dd41f71a595a',
                    },
                    allDisciplineData?.[0],
                    true
                )
                if (!isBetaWorkspaceToggleEnabled) {
                    categoyPanelDiv.scrollTop = categoyPanelDiv?.scrollHeight
                }
                setStateValue(
                    'elementsInProjectFilterWidgetActive',
                    false,
                    true
                )
            } else if (
                selectedMenuItem?.value === ADDED_TO_PROJECT ||
                selectedMenuItem?.value === ADDED_TO_DRAFT
            ) {
                if (
                    selectedCategory?.name === 'Work Results' &&
                    autoCategorySelection
                ) {
                    handleSelectedCatgoryChange(
                        {
                            name: 'All Categories',
                            id: 'all',
                        },
                        allDisciplineData?.[0],
                        true
                    )
                    categoyPanelDiv.scrollTop = 0
                }
                setStateValue('elementsInProjectFilterWidgetActive', true, true)
            } else if (
                selectedMenuItem?.value === ALL_PROJECTS_ELEMENTS_TEXT ||
                selectedMenuItem?.value === ALL_ELEMENTS_TEXT
            ) {
                if (
                    selectedCategory?.name === 'Work Results' &&
                    autoCategorySelection
                ) {
                    handleSelectedCatgoryChange(
                        {
                            name: 'All Categories',
                            id: 'all',
                        },
                        allDisciplineData?.[0],
                        true
                    )
                    if (categoyPanelDiv) {
                        categoyPanelDiv.scrollTop = 0
                    }
                }
                setStateValue(
                    'elementsInProjectFilterWidgetActive',
                    false,
                    true
                )
            }
        },
        [
            currentState,
            setStateValue,
            allDisciplineData,
            selectedCategory,
            autoCategorySelection,
            selectedElementFilterType,
            skipDefaultQuery,
            isBetaWorkspaceToggleEnabled,
        ]
    )

    const selectedNodeFromBP = React.useMemo(
        () =>
            isOMUser &&
            getPersistedState?.('elementIdFromBP')?.bestPracticeProjectId ===
                currentUserActiveFirm?.bestPracticeProjectId
                ? getPersistedState?.('elementIdFromBP')?.projectElementNodeId
                : undefined,
        [getPersistedState, isOMUser, currentUserActiveFirm]
    )

    const familyNumber = React.useMemo(
        () =>
            isOMUser && getPersistedState?.('elementIdFromBP')?.familyNumber
                ? getPersistedState?.('elementIdFromBP')?.familyNumber
                : '',
        [getPersistedState, isOMUser]
    )

    const familyName = React.useMemo(() => {
        return isOMUser && getPersistedState?.('elementIdFromBP')?.familyName
            ? getPersistedState?.('elementIdFromBP')?.familyName
            : ''
    }, [getPersistedState, isOMUser])

    const selectedItemIdFromStudioTable = React.useMemo(
        () =>
            isOMUser &&
            getPersistedState?.('elementIdFromBP')?.projectElementNodeId
                ? getPersistedState?.('elementIdFromBP')?.projectElementNodeId
                : undefined,
        [getPersistedState, isOMUser]
    )

    const onlyElementsAssignedToProjectFilter = React.useMemo(() => {
        return isFromRedirect || previousIsRedirect
            ? true
            : !!currentState['elementsInProjectFilterWidgetActive'] ||
                  selectedElementFilterType?.type === ADDED_TO_PROJECT
    }, [currentState, isFromRedirect, previousIsRedirect])
    const previousElementsAssignedToProjectFilter = usePrevious(
        !!onlyElementsAssignedToProjectFilter
    )

    const [propertyValue, setPropertyValue] = React.useState<
        Record<string, string>
    >({})

    const [selectedSpecAgentIdPt, setSelectedSpecAgentIdPt] = React.useState<
        string
    >('')

    const [BPElementTreePrams, setBPElementTreePrams] = React.useState<{
        id: string
        treepath: string
        isBP: boolean
        isGBP: boolean
        remove: boolean
    } | null>(null)

    const [
        getMoreElementListInput,
        setGetMoreElementListInput,
    ] = useState<GetMoreElementListInputType | null>()

    // The getPersistedNode callback returns the persisted element based on the isAddedToProject filter
    const getPersistedNode = React.useCallback(() => {
        const isDivisionViewOpen = fromStudioValue
            ? isDivisionViewNodeValue
            : selectedElementFilterType?.type === 'DIVISIONS'
        const previousAddedToProjectParentElement =
            getPersistedState(
                isDivisionViewOpen
                    ? PREVIOUS_ADDEDTOPROJECT_PARENT_DIVISIONS
                    : PREVIOUS_ADDEDTOPROJECT_PARENT_ASSEMBLIES
            ) ?? undefined
        const persistedElement =
            getPersistedState(
                isDivisionViewOpen
                    ? PERSISTED_ELEMENT_DIVISIONS
                    : PERSISTED_ELEMENT_ASSEMBLIES
            ) ?? undefined
        const isAddedToProject =
            selectedElementFilterType?.value !== ALL_PROJECTS_ELEMENTS_TEXT &&
            (onlyElementsAssignedToProjectFilter ||
                selectedElementFilterType?.value === 'Added to Work Results')

        if (previousAddedToProjectParentElement && isAddedToProject) {
            persistState?.(
                isDivisionViewOpen
                    ? PERSISTED_ELEMENT_DIVISIONS
                    : PERSISTED_ELEMENT_ASSEMBLIES,
                previousAddedToProjectParentElement
            )
            return {
                ...previousAddedToProjectParentElement,
            }
        } else if (persistedElement) {
            return { ...persistedElement }
        }
        return undefined
    }, [
        selectedElementFilterType,
        onlyElementsAssignedToProjectFilter,
        persistState,
        fromStudioValue,
        isDivisionViewNodeValue,
        getPersistedState,
    ])

    const [getChildElementsList] = useLazyQuery<
        GetProjectElementNodesResponse,
        GetProjectElementNodesInput
    >(GetProjectElementNodesQuery, {
        fetchPolicy: 'no-cache',
        displayName: 'GetChildElementsList',
        onCompleted: (data) => {
            if (data) {
                updateBPElementTreeChange(
                    data.getProjectElementNodes.projectElementNodes ?? []
                )
            }
        },
    })

    const [
        getProjectElementNodesQuery,
        {
            loading: getProjectElementNodesLoading,
            networkStatus: getProjectElementNodesNetworkStatus,
        },
    ] = useLazyQuery<
        | GetProjectElementNodesResponse
        | GetOMProjectElementNodesResponseForWorkspace
        | null,
        GetProjectElementNodesInput | GetOMProjectElementNodesInput
    >(
        isAEUserProjectWorkspace
            ? isBetaWorkspaceToggleEnabled
                ? GetProjectElementNodesWithMetadataForAEProjectWSQuery
                : GetProjectElementNodesForAEProjectWSQuery
            : GetProjectElementNodesQuery,
        {
            fetchPolicy: 'no-cache',
            displayName: 'GetMoreProjectElementNodes',
            onCompleted: (data) => {
                if (data && getMoreElementListInput) {
                    updateCategoryElementState(
                        data,
                        getMoreElementListInput?.forceReset ?? false,
                        !isContentTeamUser
                            ? getMoreElementListInput.parentNodeId
                            : ''
                    )
                    handleRefreshProjectElementNodes?.(false)
                    // this block is to update bulk add items after elements list is updated
                    if (getMoreElementListInput.isBulkSelectRequested) {
                        const updateBulkAddItems = setTimeout(() => {
                            // Flag the parent node id for bulk select in the tree
                            setParentNodeIdPendingBulkSelect(
                                getMoreElementListInput.parentNodeId ?? ''
                            )
                            setIsBulkAddDataLoading(false)

                            clearTimeout(updateBulkAddItems)
                        }, 0)
                    }

                    // delays the reset
                    setTimeout(() => {
                        setShowElementsPanelFullLoadState(false)
                        setFetchAllElements(false)
                        setFromStudiovalue(false)
                        setIsFetchingMoreElements(false)
                        setIsNewDataLoading(false)
                        setGetMoreElementListInput(null)
                    }, 0)
                }
            },
            onError: () => {
                showGlobalSnackbar({
                    variables: {
                        message: 'Error on fetching project element Node',
                        variant: 'error',
                        id: `WorkspacePage_projectElementNodesListingError`,
                    },
                })
                setIsGetProjectElementNodesError(true)
            },
        }
    )

    const projectElementNodesQueryApi = React.useMemo(() => {
        switch (true) {
            case isOMWorkspace:
                return GetElementNodesForOfficeMasterWorkspaceQuery
            case isAEUserProjectWorkspace:
                return isBetaWorkspaceToggleEnabled
                    ? GetProjectElementNodesWithMetadataForAEProjectWSQuery
                    : GetProjectElementNodesForAEProjectWSQuery
            default:
                return GetProjectElementNodesQuery
        }
    }, [isOMWorkspace, isAEUserProjectWorkspace, isBetaWorkspaceToggleEnabled])

    const {
        loading: elementsListLoading,
        networkStatus: elementsListNetworkStatus,
    } = useQuery<
        | GetProjectElementNodesResponse
        | GetOMProjectElementNodesResponseForWorkspace
        | null,
        GetProjectElementNodesInput | GetOMProjectElementNodesInput
    >(projectElementNodesQueryApi, {
        fetchPolicy: 'no-cache',
        nextFetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
        variables: {
            input: isOMWorkspace
                ? {
                      projectId:
                          projectId == 'DraftSpecTextProject'
                              ? 'DraftMasterSpecProject'
                              : projectId,
                      parentNodeId:
                          fromStudioValue || selectedNodeFromBP
                              ? projectElementNodeId ?? selectedNodeFromBP
                              : !isFromRedirect
                              ? 'root'
                              : undefined,
                  }
                : {
                      projectId:
                          projectId == 'DraftSpecTextProject'
                              ? 'DraftMasterSpecProject'
                              : projectId,
                      limit: MaxElementsPerBatch,
                      skip: 0,
                      categoryId: selectedCategory
                          ? selectedCategory?.id === 'all'
                              ? null
                              : selectedCategory?.id
                          : initiallySelectedCategory?.id === 'all'
                          ? null
                          : initiallySelectedCategory?.id,
                      onlyElementsAssignedToProject: isFromRedirect
                          ? true
                          : selectedElementFilterType?.value !==
                                ALL_PROJECTS_ELEMENTS_TEXT &&
                            (onlyElementsAssignedToProjectFilter ||
                                selectedElementFilterType?.value ===
                                    'Added to Work Results'),
                      projectElementType: Object.keys(
                          ProjectElementType
                      ) as ProjectElementType[],
                      disciplineId: !getPersistedState?.('searchFilters')
                          ? initiallySelectedDiscipline?.id
                          : undefined,
                      searchFilters: !isFromRedirect
                          ? searchFilters ??
                            getPersistedState?.('searchFilters')
                          : undefined,
                      parentNodeId:
                          fromStudioValue || selectedNodeFromBP
                              ? projectElementNodeId ?? selectedNodeFromBP
                              : !isFromRedirect
                              ? 'root'
                              : undefined,
                      elementId: isFromRedirect
                          ? selectedElementIdFromDesignProgressTable
                          : undefined,
                      isProductFamilyGroupView: fromStudioValue
                          ? isDivisionViewNodeValue
                          : selectedElementFilterType?.type === 'DIVISIONS',
                      isFromDasboardView:
                          fromStudioValue || selectedNodeFromBP
                              ? true
                              : undefined,
                      persistedElementNodeId:
                          !isContentTeamUser &&
                          !searchFilters &&
                          !getPersistedState?.('searchFilters')
                              ? getPersistedNode()?.id
                              : undefined,
                      isFromAEWorkSpace: !contentTeamFromStudioPage,
                      skipChildNodeChecks: !!(
                          searchFilters ?? getPersistedState?.('searchFilters')
                      ),
                  },
        } as GetProjectElementNodesInput | GetOMProjectElementNodesInput,
        skip: skipDefaultQuery || !currentUserId,
        onCompleted: async (
            data:
                | GetProjectElementNodesResponse
                | GetOMProjectElementNodesResponseForWorkspace
                | null
        ) => {
            if (
                Object.keys(categoryElementStates).length === 0 ||
                isFromRedirect
            ) {
                const isOMData = isOMWorkspace && data
                const elementNodes = isOMData
                    ? (data as GetOMProjectElementNodesResponseForWorkspace)
                          ?.getElementNodesForOfficeMasterWorkspace
                          ?.projectElementNodes
                    : (data as GetProjectElementNodesResponse)
                          ?.getProjectElementNodes?.projectElementNodes

                const isLargeDataset =
                    isOMData &&
                    elementNodes &&
                    elementNodes.length > OM_CHUNK_SIZE

                if (isLargeDataset) {
                    // Process in chunks to prevent UI freeze
                    setIsProcessingChunks(true)

                    let processedNodes: typeof elementNodes = []

                    await processDataInChunks(
                        elementNodes!,
                        OM_CHUNK_SIZE,
                        (chunk, isLastChunk) => {
                            processedNodes = [...processedNodes, ...chunk]

                            if (isLastChunk) {
                                // Create modified data with processed chunks
                                const processedData = {
                                    ...data,
                                    getElementNodesForOfficeMasterWorkspace: {
                                        ...(data as GetOMProjectElementNodesResponseForWorkspace)
                                            .getElementNodesForOfficeMasterWorkspace,
                                        projectElementNodes: processedNodes,
                                    },
                                } as GetOMProjectElementNodesResponseForWorkspace

                                updateCategoryElementState(
                                    processedData,
                                    undefined,
                                    !isContentTeamUser
                                        ? fromStudioValue || selectedNodeFromBP
                                            ? projectElementNodeId ??
                                              selectedNodeFromBP
                                            : !isFromRedirect
                                            ? 'root'
                                            : undefined
                                        : ''
                                )
                                setShowElementsPanelFullLoadState(false)
                                setSkipDefaultQuery(true)
                                setIsProcessingChunks(false)
                            }
                        }
                    )
                } else {
                    // Normal processing for small datasets or non-OM workspace
                    updateCategoryElementState(
                        data!,
                        undefined,
                        !isContentTeamUser
                            ? fromStudioValue || selectedNodeFromBP
                                ? projectElementNodeId ?? selectedNodeFromBP
                                : !isFromRedirect
                                ? 'root'
                                : undefined
                            : ''
                    )
                    setShowElementsPanelFullLoadState(false)
                    setSkipDefaultQuery(true)
                }
            }
        },
    })

    const {} = useQuery<
        GetFamilyNodesBySustainabilityTopicResponse | null,
        GetFamilyNodesBySustainabilityTopicInput
    >(GetFamilyNodesBySustainabilityTopicQuery, {
        fetchPolicy: 'no-cache',
        nextFetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
        variables: {
            input: {
                limit: 300,
                skip: 0,
                projectId: projectId,
                projectElementType: Object.keys(
                    ProjectElementType
                ) as ProjectElementType[],
                sustainabilityTopicId: selectedTopic?.productFamilyId,
            },
        },
        skip: selectedTopic === undefined,
        onCompleted: (data) => {
            setSustanaibilityTopicFamilies(
                data?.getFamilyNodesBySustainabilityTopic?.projectElementNodes
            )
        },
    })

    const [
        getProjectUniformatClassification,
        {
            error: projectUniformatClassificationError,
            data: projectUniformatClassificationData,
        },
    ] = useLazyQuery<GetProjectUniformatClassificationResponse>(
        GetProjectUniformatClassification,
        {
            fetchPolicy: 'no-cache',
            displayName: 'ProjectUniformatClassification',
        }
    )

    const [
        getUniformatClassification,
        {
            data: uniformatClassificationData,
            error: uniformatClassificationError,
        },
    ] = useLazyQuery<GetUniformatClassificationResponse>(
        GetUniformatClassification,
        {
            fetchPolicy: 'no-cache',
            displayName: 'UniformatClassification',
        }
    )

    const [
        getProjectUniformatClassificationElement,
        {
            error: projectUniformatClassificationElementError,
            data: projectUniformatClassificationElementData,
        },
    ] = useLazyQuery<GetProjectUniformatClassificationElementResponse>(
        GetProjectUniformatClassificationElement,
        {
            fetchPolicy: 'no-cache',
            displayName: 'ProjectUniformatClassification',
        }
    )

    const executeSearch = React.useCallback(
        (input: GetProjectElementNodesInput) => {
            setIsWhitespaceSearch(false)
            setSearchFilters(input.input.searchFilters)
            setSkipDefaultQuery(true)
            setShowElementsPanelFullLoadState(true)
            persistState?.('searchFilters', input.input.searchFilters)
        },
        [persistState]
    )

    /**
     * Render the "No sections match your search" empty state for a
     * whitespace/dash-only query without firing the backend query (which
     * rejects empty search text).
     */
    const handleWhitespaceSearch = React.useCallback(() => {
        const categoryId = selectedCategory?.id ?? 'all'
        setIsWhitespaceSearch(true)
        setSkipDefaultQuery(true)
        setShowElementsPanelFullLoadState(false)
        setCategoryElementStates((prev) => ({
            ...prev,
            [categoryId]: { ...prev[categoryId], data: [] },
        }))
    }, [selectedCategory, setCategoryElementStates])

    const resetSearchFilters = () => {
        if (searchFilters || isWhitespaceSearch) {
            setShowElementsPanelFullLoadState(true)
        }
        setSearchFilters(undefined)
        setIsWhitespaceSearch(false)
        setSkipDefaultQuery(true)
        persistState?.('searchFilters', undefined)
    }

    const getUpdatedTreeIndexer = React.useCallback(
        (
            elementData: ProjectElementNode[],
            currentElementFlatList: CategoryElementListStateInfo[],
            isDivisionSelected?: boolean
        ) => {
            const data = isDivisionSelected
                ? updatedDivisionElements(
                      elementData,
                      categoryElementStates?.[selectedCategory?.id ?? 'all']
                          ?.data
                  )
                : elementData
            const currentTreeIndexer = treeIndexerRef.current
            data?.forEach((each: ProjectElementNode, index: number) => {
                if (!currentTreeIndexer.has(each.treePath)) {
                    currentTreeIndexer.set(
                        each.treePath,
                        new Map<string, number>()
                    )
                }
                const categoryIndexer = currentTreeIndexer.get(each.treePath)
                categoryIndexer?.set(
                    selectedCategory?.id!,
                    currentElementFlatList.length + index
                )
            })
            return currentTreeIndexer
        },
        [
            treeIndexerRef,
            selectedCategory,
            updatedDivisionElements,
            selectedCategory,
            categoryElementStates,
        ]
    )

    const getUpdatedElementDataForOMWorkspace = React.useCallback(
        (data: GetOMProjectElementNodesResponseForWorkspace) => {
            const initialData = data?.getElementNodesForOfficeMasterWorkspace
            return {
                ...initialData,
                projectElementNodes: getSortedList(
                    initialData?.projectElementNodes,
                    'nodeOrder'
                ),
            }
        },
        [isOMWorkspace]
    )

    const updateCategoryElementState = React.useCallback(
        (
            data:
                | GetProjectElementNodesResponse
                | GetOMProjectElementNodesResponseForWorkspace,
            forceReset: boolean = false,
            parentNodeId: string | undefined = undefined
        ) => {
            const isDivision = selectedElementFilterType?.type === 'DIVISIONS'
            const currentCategoryElementStates = Object.assign(
                elementStates && !forceReset && !isFromRedirect
                    ? elementStates
                    : {
                          data: null,
                          hasMoreElementsToFetch: false,
                          totalElementCount: 0,
                          elementSkipCount: 0,
                      }
            )

            const currentElementFlatList = [
                ...(forceReset
                    ? []
                    : isDivision
                    ? updatedDivisionElements(
                          currentCategoryElementStates.data
                      ) ?? []
                    : currentCategoryElementStates.data ?? []),
            ]

            //reset treeIndexer too if forceReset is true
            if (forceReset) {
                treeIndexerRef.current.clear()
            }
            const currentElementData = isOMWorkspace
                ? getUpdatedElementDataForOMWorkspace(
                      data as GetOMProjectElementNodesResponseForWorkspace
                  )
                : (data as GetProjectElementNodesResponse)
                      ?.getProjectElementNodes
            treeIndexerRef.current = getUpdatedTreeIndexer(
                currentElementData?.projectElementNodes,
                currentElementFlatList,
                selectedElementFilterType?.type === 'DIVISIONS'
            )
            const isForSearch: boolean = !isFromRedirect && !!searchFilters
            const childProductTypes =
                selectedElementFilterType?.type === 'DIVISIONS'
                    ? updatedDivisionElements(
                          currentElementData?.projectElementNodes,
                          currentElementFlatList
                      )
                    : currentElementData?.projectElementNodes

            const parentIndex =
                currentElementFlatList.findIndex(
                    (element) =>
                        element?.id === childProductTypes?.[0]?.parentNodeId ||
                        element?.treePath ===
                            childProductTypes?.[0]?.parentTreePath
                ) + 1
            const concatenatedList = !isForSearch
                ? [
                      ...(currentCategoryElementStates.elementSkipCount > 0
                          ? currentElementFlatList
                          : []
                      ).slice(0, parentIndex),
                      ...childProductTypes,
                      ...(currentCategoryElementStates.elementSkipCount > 0
                          ? currentElementFlatList
                          : []
                      ).slice(parentIndex),
                  ]
                : [
                      ...(currentCategoryElementStates.elementSkipCount > 0
                          ? currentElementFlatList
                          : []),
                      ...childProductTypes,
                  ]

            currentCategoryElementStates.hasMoreElementsToFetch = isForSearch
                ? currentElementData?.totalCount > concatenatedList.length
                : false
            currentCategoryElementStates.elementSkipCount =
                (currentCategoryElementStates.elementSkipCount ?? 0) + 1
            // Fix: propagate isInProject: true from leaf PT nodes up to their ancestor
            // Section/Division/Assembly/Family nodes. The server only marks PT-level nodes as
            // isInProject — ancestors must be promoted client-side so the green highlight
            // bubbles up the tree on refresh and after expansion. Applies to both Division
            // and Assembly views (safe to call unconditionally — returns original reference
            // when no ancestors need updating).
            currentCategoryElementStates.data = propagateIsInProjectToAncestors(
                concatenatedList
            )
            currentCategoryElementStates.totalElementCount =
                currentElementData?.totalCount

            const baseVal = forceReset ? {} : categoryElementStates
            const newVal = {
                ...baseVal,
                [selectedCategory?.id ?? 'all']: currentCategoryElementStates,
            }

            setCategoryElementStates(newVal)
            const isInitialLoadForCategory = currentElementFlatList.length === 0

            if (isInitialLoadForCategory && !isFromRedirect) {
                // Fix: in Division view use the Division-format treePath from childProductTypes
                // (remapped by updatedDivisionElements) rather than the raw Assembly-format
                // treePath from the backend response. The tree's selected check compares
                // node.id (Division-format) against selectedElementIdentifier.treePath, so
                // they must match in format.
                const firstElement = {
                    baseElementId:
                        currentElementData?.projectElementNodes[0]
                            ?.baseElementId,
                    treePath: isDivision
                        ? childProductTypes?.[0]?.treePath
                        : currentElementData?.projectElementNodes[0]?.treePath,
                    id:
                        currentElementData?.projectElementNodes[0]?.id ??
                        currentElementData?.projectElementNodes[0]?.elementId,
                }

                //    This below code selects the persisted or first element in element tree view
                if (
                    parentNodeId === 'root' &&
                    getPersistedNode()?.id &&
                    currentElementData.projectElementNodes.some(
                        (node) => node.id === getPersistedNode()?.id
                    )
                ) {
                    setSelectedElementIdentifier(getPersistedNode())
                } else {
                    if (parentNodeId === 'root') {
                        // Fix: in Division view, do NOT overwrite a persisted Section/PT node
                        // with firstElement. Section/PT UUIDs are not in the initial 35-node
                        // batch so some() always fails for them, but the persisted data is still
                        // valid and should survive the refresh. Overwriting it would corrupt
                        // PERSISTED_ELEMENT_DIVISIONS and cause every subsequent refresh to
                        // select the wrong node.
                        const shouldPersistFirstElement =
                            !isDivision || !getPersistedNode()?.id
                        if (shouldPersistFirstElement) {
                            persistState?.(
                                isDivision
                                    ? PERSISTED_ELEMENT_DIVISIONS
                                    : PERSISTED_ELEMENT_ASSEMBLIES,
                                firstElement
                            )
                        }
                    }
                    setSelectedElementIdentifier(firstElement)
                }
            }
        },
        [
            categoryElementStates,
            elementStates,
            selectedCategory,
            setCategoryElementStates,
            setSelectedElementIdentifier,
            isFromRedirect,
            treeIndexerRef,
            getUpdatedTreeIndexer,
            searchFilters,
            selectedElementFilterType,
            updatedDivisionElements,
            getPersistedNode,
            isOMWorkspace,
        ]
    )

    const { loading: categoryLoading } = useQuery<CategoryPanelResponse>(
        CategoriesQuery,
        {
            fetchPolicy: 'no-cache',
            variables: {
                id: '4e3d44c1-ec50-40c3-8783-1983a905a8a9',
            },
            onCompleted: (data: CategoryPanelResponse) => {
                let dataDiscipline: ProfessionalDisciplineInner[] = []
                dataDiscipline = data?.firm?.professionalDisciplines
                dataDiscipline = [
                    dataDiscipline[dataDiscipline.length - 1],
                    ...dataDiscipline,
                ]

                dataDiscipline.splice(dataDiscipline.length - 1, 1)
                setData(dataDiscipline)
            },
        }
    )

    const {
        data: selectedProductsCount,
        refetch: refetchSelectedProductsCount,
        networkStatus: selectedProductsCountNetworkStatus,
    } = useQuery<GetProjectManufacturerProductListingResponse>(
        GetProjectManufacturerProductListingQuery,
        {
            displayName: 'ProjectProductListing',
            fetchPolicy: 'no-cache',
            variables: {
                input: {
                    projectId,
                    projectElementType: ProjectElementType.PRODUCTTYPE, // can be PRODUCTFAMILY, ASSEMBLIES, etc\
                } as GetProjectManufacturerProductListingInput,
            },
            onCompleted: (data) => {
                setProjectProductListingCount(
                    data?.getProjectManufacturerProductListing?.length ?? 0
                )
            },
            onError: () => {
                showGlobalSnackbar({
                    variables: {
                        message: 'Error on fetching project product listing',
                        variant: 'error',
                        id: `ProductListingsPage_ProjectProductListingError`,
                    },
                })
            },
        }
    )

    const [
        addedElememtsToProject,
        {
            data: elementsAddedToProject,
            refetch: refetchAddedElementsCount,
            networkStatus: elementsAddedToProjectNetworkStatus,
        },
    ] = useLazyQuery<
        GetProjectElementsTotalCountResponse,
        GetProjectElementNodeCountInput
    >(GetProjectElementsTotalCountQuery, {
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: true,
    })

    const [saveUpdatedProjectSettings] = useMutation<{
        projectSettingsInput: ProjectSettingsInput
        setProjectSettings: UpdateProjectSettingsResponse
    }>(UpdateProjectSettings, {
        onCompleted: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'Project settings changes saved successfully',
                    variant: 'success',
                    id: `ProjectSettingsDialog_SuccessSaving`,
                },
            })
        },
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'Error updating project settings',
                    variant: 'error',
                    id: `ProjectSettingsDialog_ErrorSaving`,
                },
            })
        },
    })

    const [updatePUCPropertyValues] = useMutation<
        UpdatePUCPropertyValuesResponse,
        { input: UpdatePropertiesForPUCInput }
    >(UpdatePropertyValuesForUniformatClassificationMutation, {
        onCompleted: () => {
            showGlobalSnackbar?.({
                variables: {
                    message: 'Changes Saved Successfully',
                    variant: 'success',
                    id: `WorkspacePage-IpdatePUCPropertyValuesSuccess`,
                },
            })
        },
        onError: (error) => {
            showGlobalSnackbar({
                variables: {
                    message: error.message ?? '',
                    variant: 'error',
                    id: 'ProjectWorkspaceContentArea_ProjectElementProperties',
                },
            })
        },
    })

    const [updateEditingTime] = useMutation<UpdateEditingTimeInput>(
        UpdateEditingTime
    )

    const onTriggerSearching = React.useCallback(
        (toggleDisplay?: boolean) => {
            setIsSearchInitiated(toggleDisplay)
            persistState?.('searchBarDisplayed', toggleDisplay)
            setIsSearchBarDisplayed?.(toggleDisplay ?? false)
        },
        [setIsSearchInitiated, persistState, setIsSearchBarDisplayed]
    )

    const onToggleShowHideBarDisplay = React.useCallback(
        (toggleDisplay?: boolean) => {
            setIsToggleBarVisible(toggleDisplay)
            persistState?.('showHideBarDisplay', toggleDisplay)
        },
        [setIsToggleBarVisible, persistState]
    )

    const handleSaveSettings = (input: ProjectSettingsInput) => {
        setProjectSettings(input)
        saveUpdatedProjectSettings({ variables: { input } })
    }

    const handleUpdatePUCPropertyValue = React.useCallback(
        (elementDataId: string) => {
            if (selectedProjectElementNode) {
                if (!selectedProjectElementNode.elementId) return
                updatePUCPropertyValues({
                    variables: {
                        input: {
                            projectUniformatClassificationId:
                                selectedProjectElementNode.elementId,
                            uniformatClassificationPropertyInputs: [
                                {
                                    id: elementDataId,
                                    value: propertyValue[elementDataId] ?? '',
                                },
                            ],
                            projectId: projectId ?? '',
                        },
                    },
                })
            }
        },
        [
            selectedProjectElementNode,
            propertyValue,
            projectId,
            updatePUCPropertyValues,
        ]
    )

    const showHideStates = React.useMemo(
        () =>
            getNestedObjectPropertyValue('showHideStates', currentState) ?? {},
        [currentState]
    )

    const areElementsLoadingOrNotReady: boolean = React.useMemo(() => {
        return (
            elementsListLoading ||
            getProjectElementNodesLoading ||
            elementsListNetworkStatus !== NetworkStatus.ready ||
            getProjectElementNodesNetworkStatus !== NetworkStatus.ready
        )
    }, [
        elementsListLoading,
        getProjectElementNodesLoading,
        elementsListNetworkStatus,
        getProjectElementNodesNetworkStatus,
    ])

    React.useEffect(() => {
        // this block is to set division view as default view when project loads initially
        if (
            isBetaWorkspaceToggleEnabled &&
            !getPersistedState?.('elementPanelFilterType')
        ) {
            setSelectedElementFilterType({
                type: 'DIVISIONS',
                value: contentTeamFromStudioPage
                    ? ALL_ELEMENTS_TEXT
                    : ALL_PROJECTS_ELEMENTS_TEXT,
            })
        }
    }, [
        initialElementFilterType,
        getPersistedState,
        contentTeamFromStudioPage,
        isBetaWorkspaceToggleEnabled,
    ])

    React.useEffect(() => {
        if (!_isMounted) {
            setElementItemValue({
                ...elementItemValue,
                elementId: '',
                elementType: '',
                baseElementId: '',
            })
            _setIsMounted(true)
        }
    }, [_isMounted])

    // Sync selectedElementFilterType to ElementItemStore for PT cache optimization
    React.useEffect(() => {
        if (selectedElementFilterType?.value && setSelectedElementFilterValue) {
            setSelectedElementFilterValue(selectedElementFilterType.value)
        }
    }, [selectedElementFilterType, setSelectedElementFilterValue])

    React.useEffect(() => {
        //update elements list after best practices progress completed while creating project
        if (refreshProjectElementNodes) {
            getMoreElementsList(true, 'root')
        }
    }, [refreshProjectElementNodes])
    React.useEffect(() => {
        setIsTrackChangesToggleOn(showHideStates.changes)
        const savedSearchBarState = getPersistedState?.('searchBarDisplayed')

        // set initial value for isSearchBarDisplayed on load
        if (savedSearchBarState !== undefined) {
            setIsSearchBarDisplayed?.(savedSearchBarState)
        }
    }, [showHideStates.changes, setIsSearchBarDisplayed, getPersistedState])
    React.useEffect(() => {
        if (
            currentUserActiveFirm &&
            (isFromRedirect ||
                projectId !== currentUserActiveFirm?.bestPracticeProjectId)
        ) {
            persistState?.('elementIdFromBP', undefined)
        }
    }, [persistState, currentUserActiveFirm, projectId, isFromRedirect])

    React.useEffect(() => {
        if (!isOMWorkspace) return

        if (elementsListLoading) {
            // Reset timeout state when loading starts
            setOmQueryTimeout(false)
            setOmQueryTimeoutSeconds(0)

            // Start counting seconds
            omTimeoutTimersRef.current.interval = setInterval(() => {
                setOmQueryTimeoutSeconds((prev) => prev + 1)
            }, 1000)

            // Set timeout to show warning after OM_QUERY_TIMEOUT_MS
            omTimeoutTimersRef.current.timeout = setTimeout(() => {
                setOmQueryTimeout(true)
            }, OM_QUERY_TIMEOUT_MS)
        } else {
            // Reset when loading completes
            setOmQueryTimeout(false)
            setOmQueryTimeoutSeconds(0)
        }

        return () => {
            if (omTimeoutTimersRef.current.timeout)
                clearTimeout(omTimeoutTimersRef.current.timeout)
            if (omTimeoutTimersRef.current.interval)
                clearInterval(omTimeoutTimersRef.current.interval)
        }
    }, [isOMWorkspace, elementsListLoading])

    const handleViewAllProducts = React.useCallback(() => {
        const saved = getPersistedState('projectProductDialogOpened')
        const family = getPersistedState('selectedFamily')
        const productType = getPersistedState('selectedProductType')
        const nodeId = getPersistedState('selectedNodeId')
        const elementType = getPersistedState('selectedElementType')
        const elementId = getPersistedState('selectedElementId')
        const isLastSubAssembly = getPersistedState('isLastSubAssembly')
        const assemblyPath = getPersistedState('selectedAssemblyPath')
        const parentNodeId = getPersistedState('selectedParentNodeId')
        const divisionNumber = getPersistedState('divisionNumber')
        const selectedFilterType = getPersistedState('selectedFilterType')
        const categoryNodeId = getPersistedState('categoryNodeId')
        // do below if windows is opened

        if (productDialogWindow !== null) {
            productDialogWindow.close()
        }

        productDialogWindow = window.open(
            getDialogUrl(
                {
                    category: selectedCategory,
                    family,
                    productType,
                    projectId,
                    elementNode: selectedProjectElementNode,
                    nodeId,
                    elementId,
                    elementType,
                    specAgentIdPt: selectedSpecAgentIdPt,
                    isLastSubAssembly,
                    assemblyPath,
                    parentNodeId,
                    divisionNumber,
                    selectedFilterType,
                    categoryNodeId,
                } as WorkspaceProductListing,
                'dialog'
            ),
            `ProductListing${projectId}`,
            'directories=no, titlebar=no, location=no, toolbar=no, menubar=no, resizable=yes, width=1300, height=1000'
        )
    }, [
        getPersistedState,
        selectedSpecAgentIdPt,
        selectedCategory,
        projectId,
        selectedProjectElementNode,
        selectedSpecAgentIdPt,
    ])
    const callViewNotes = React.useCallback(() => {
        handleViewNotes({
            projectId: projectId ?? '',
            getPersistedState: getPersistedState,
            selectedProjectElementNode: selectedProjectElementNode,
        })
    }, [projectId, selectedProjectElementNode, getPersistedState])

    // get the family id value based on selected element
    const getFamilyIdFromText = React.useCallback(
        (elementType: string, text: string, parentTreePath: string) => {
            if (elementType === 'PRODUCTTYPE') {
                const familyName = parentTreePath.slice(
                    parentTreePath.lastIndexOf('||') + 2
                )
                return familyName.slice(0, familyName.indexOf('|'))
            }
            return text.slice(0, text.indexOf('|'))
        },
        [selectedProjectElementNode]
    )

    // get the input to update time spent
    const getUpdateTimeInput = React.useCallback(
        (totalTime: number) => {
            const {
                elementId,
                elementType,
                text,
                parentTreePath,
            } = selectedProjectElementNode!
            const productTypeName = text.slice(text.lastIndexOf('|') + 1)
            const familyId = getFamilyIdFromText(
                elementType,
                text,
                parentTreePath
            )
            return {
                elementId,
                elementType,
                productTypeName:
                    elementType === 'PRODUCTTYPE' ? productTypeName : null,
                editingTimeSpan: totalTime,
                familyId,
                updateType: 'UPDATEDDRAFTVERSION',
            }
        },
        [selectedProjectElementNode]
    )

    const handleElementItemCardClick = React.useCallback(
        (newSelectedElementIdentifier?: ElementItemIdentifier) => {
            handleUpdateSelectedTopic(undefined)
            setSelectedSpecAgentIdPt('')
            setSelectedElementIdentifier(newSelectedElementIdentifier)
            persistState?.(
                selectedElementFilterType?.type === 'DIVISIONS'
                    ? PERSISTED_ELEMENT_DIVISIONS
                    : PERSISTED_ELEMENT_ASSEMBLIES,
                newSelectedElementIdentifier
            )

            if (
                isUserTrackable &&
                selectedProjectElementNode?.elementType !== 'ASSEMBLY'
            ) {
                const endTime = new Date().getTime()
                const totalTime = endTime - startTime!
                const input = getUpdateTimeInput(totalTime)

                updateEditingTime({ variables: { input } })
            }

            setStartTime(new Date().getTime())
        },
        [
            setSelectedElementIdentifier,
            setSelectedSpecAgentIdPt,
            selectedElementIdentifier,
            startTime,
            persistState,
            selectedElementFilterType,
            selectedProjectElementNode,
            isUserTrackable,
        ]
    )

    React.useEffect(() => {
        if (!startTime) {
            setStartTime(new Date().getTime())
        }
    }, [startTime])

    React.useEffect(() => {
        return () => {
            document.removeEventListener(
                'visibilitychange',
                handlePostTimeSpent
            )
            handlePostTimeSpent()
        }
    }, [selectedProjectElementNode])

    React.useEffect(() => {
        document.addEventListener('visibilitychange', handlePostTimeSpent)
        return () => {
            document.removeEventListener(
                'visibilitychange',
                handlePostTimeSpent
            )
        }
    }, [startTime])

    const handlePostTimeSpent = React.useCallback(() => {
        if (!isUserTrackable) return
        const urlParts = document.URL?.split('/')
        const isWorkspace = urlParts[urlParts.length - 1] === 'workspace'

        const end = new Date().getTime()

        const totalTime = end - startTime!
        if (
            !!selectedProjectElementNode &&
            selectedProjectElementNode?.elementType !== 'ASSEMBLY' &&
            totalTime > 5000 &&
            (document.visibilityState === 'hidden' || !isWorkspace)
        ) {
            const input = getUpdateTimeInput(totalTime)
            updateEditingTime({ variables: { input } })
        }
        setStartTime(null)
    }, [startTime, selectedProjectElementNode, isUserTrackable])

    const handleSavePreviousElementForPersistance = React.useCallback(
        (elementIdentifier?: ElementItemIdentifier) => {
            persistState?.(
                selectedElementFilterType?.type === 'DIVISIONS'
                    ? PREVIOUS_ADDEDTOPROJECT_PARENT_DIVISIONS
                    : PREVIOUS_ADDEDTOPROJECT_PARENT_ASSEMBLIES,
                elementIdentifier
            )
        },
        [persistState, selectedElementFilterType]
    )

    // L4 assembly will have treepath containing its name and treePath of L3 separated by '||'.
    const isElementL4Assembly = (element: ProjectElementNode) =>
        !!element.treePath && element.treePath.split('||').length === 2

    // L5 assembly will have treepath containing treePath of L3, L4 and its name all separated by '||'
    const isElementL5Assembly = (element: ProjectElementNode) =>
        !!element.treePath && element.treePath.split('||').length === 3

    const onProjectSettingsClicked = React.useCallback(() => {
        setIsProjectSettingsOpen(!isProjectSettingsOpen)
    }, [setIsProjectSettingsOpen, isProjectSettingsOpen])

    const setNextSelectableElement = React.useCallback(
        (elementList: ProjectElementNode[], startFromIndex: number) => {
            let newSelecteableIndex: number = -1

            // first search forward
            if (startFromIndex < elementList.length) {
                for (
                    let index = startFromIndex;
                    index < elementList.length;
                    index++
                ) {
                    const currentItem = elementList[index]

                    if (
                        isElementL4Assembly(currentItem) ||
                        isElementL5Assembly(currentItem)
                    ) {
                        continue
                    }
                    newSelecteableIndex = index
                    break
                }
            }
            // if no selectable element found in forward search then search backward.
            if (newSelecteableIndex < 0) {
                if (startFromIndex >= elementList.length)
                    startFromIndex = elementList.length

                for (let index = startFromIndex - 1; index >= 0; index--) {
                    const currentItem = elementList[index]

                    if (
                        isElementL4Assembly(currentItem) ||
                        isElementL5Assembly(currentItem)
                    ) {
                        continue
                    }
                    newSelecteableIndex = index
                    break
                }
            }
            if (newSelecteableIndex >= 0) {
                const nextSelectableElement = {
                    treePath: elementList[newSelecteableIndex].treePath,
                    baseElementId:
                        elementList[newSelecteableIndex].baseElementId,
                    id:
                        elementList[newSelecteableIndex].id ??
                        elementList[newSelecteableIndex].elementId,
                } as ElementItemIdentifier
                setSelectedElementIdentifier(nextSelectableElement)
            }
        },
        [setSelectedElementIdentifier]
    )

    const getMoreElementsList = React.useCallback(
        (
            forceReset: boolean = false,
            parentNodeId?: string,
            elementId?: string,
            /**
             * Set to true if the request is coming from a bulk select action.
             * The children of {@link parentNodeId} will be loaded and then the bulk select action will be triggered,
             * adding the children to the bulk-add list.
             */
            isBulkSelectRequested: boolean = false
        ) => {
            //skip the API call if the query is already loading and there is no data in elements tree
            if (
                areElementsLoadingOrNotReady &&
                !categoryElementStates?.[selectedCategory?.id ?? 'all']?.data
            )
                return
            setSkipDefaultQuery(true)
            const isForSearch = !isFromRedirect && !!searchFilters
            const skipValueForDefault =
                forceReset || parentNodeId !== undefined
                    ? 0
                    : (elementStates?.elementSkipCount ?? 0) *
                      MaxElementsPerBatch
            const getMoreElementsListInput:
                | GetProjectElementNodesInput
                | GetOMProjectElementNodesInput = {
                input: isOMWorkspace
                    ? {
                          projectId,
                          parentNodeId,
                      }
                    : {
                          projectId: projectId,
                          limit: MaxElementsPerBatch,
                          categoryId:
                              selectedCategory?.id === 'all'
                                  ? undefined
                                  : selectedCategory?.id,
                          onlyElementsAssignedToProject:
                              selectedElementFilterType?.value !==
                                  ALL_PROJECTS_ELEMENTS_TEXT &&
                              (onlyElementsAssignedToProjectFilter ||
                                  selectedElementFilterType?.value ===
                                      'Added to Work Results'),
                          projectElementType: Object.keys(
                              ProjectElementType
                          ) as ProjectElementType[],
                          disciplineId: !isForSearch
                              ? selectedDiscipline?.id
                              : undefined,
                          parentNodeId: parentNodeId,
                          elementId: elementId,
                          isProductFamilyGroupView:
                              selectedElementFilterType?.type === 'DIVISIONS',
                          persistedElementNodeId:
                              !isContentTeamUser &&
                              !searchFilters &&
                              !getPersistedState?.('searchFilters') &&
                              parentNodeId === 'root'
                                  ? getPersistedNode()?.id
                                  : undefined,
                          skip: skipValueForDefault,
                          searchFilters: isForSearch
                              ? searchFilters
                              : undefined,
                          isFromAEWorkSpace: !contentTeamFromStudioPage,
                          skipChildNodeChecks: isForSearch,
                      },
                projectId: isAEUserProjectWorkspace ? projectId : undefined,
            } as GetProjectElementNodesInput | GetOMProjectElementNodesInput

            if (isBulkSelectRequested) {
                setIsBulkAddDataLoading(true)
            }
            setGetMoreElementListInput({
                forceReset,
                parentNodeId,
                elementId,
                isBulkSelectRequested,
            })
            getProjectElementNodesQuery({
                variables: getMoreElementsListInput,
            })
        },
        [
            areElementsLoadingOrNotReady,
            getProjectElementNodesQuery,
            updateCategoryElementState,
            handleElementItemCardClick,
            projectId,
            selectedCategory,
            selectedDiscipline,
            elementStates,
            onlyElementsAssignedToProjectFilter,
            selectedElementFilterType,
            searchFilters,
            isFromRedirect,
            skipDefaultQuery,
            getPersistedNode,
            isBulkAddDataLoading,
            isOMWorkspace,
            contentTeamFromStudioPage,
            categoryElementStates,
        ]
    )

    const handleSelectedCatgoryChange = React.useCallback(
        (
            newSelectedCategory?: CategoryPanel,
            newSelectedDiscipline?: ProfessionalDisciplineInner,
            autoSelection?: boolean
        ) => {
            if (newSelectedCategory?.id !== 'all') {
                setUpdateElementPanelState?.(true)
            }
            if (
                newSelectedCategory?.name !== 'Work Results' &&
                selectedElementFilterType?.value === 'Added to Work Results' &&
                !autoSelection
            ) {
                setSelectedElementFilterType({
                    ...selectedElementFilterType,
                    value: contentTeamFromStudioPage
                        ? ADDED_TO_DRAFT
                        : ADDED_TO_PROJECT,
                })

                setStateValue(
                    'elementPanelFilterType',
                    {
                        ...selectedElementFilterType,
                        value: contentTeamFromStudioPage
                            ? ADDED_TO_DRAFT
                            : ADDED_TO_PROJECT,
                    },
                    true
                )
                setStateValue('elementsInProjectFilterWidgetActive', true, true)
            }

            setSelectedCategory(newSelectedCategory)
            persistState?.('selectedCategory', newSelectedCategory)
            if (newSelectedDiscipline) {
                setSelectedDiscipline(newSelectedDiscipline)
                persistState?.('selectedDiscipline', newSelectedDiscipline)
            }
            setAutoaCategorySelection(autoSelection ?? false)
            const nodeId = getPersistedState('selectedNodeId')
            const elementId = getPersistedState('selectedElementId')
            if (
                !categoryElementStates?.[newSelectedCategory?.id ?? ''] ||
                selectedDiscipline?.id !== newSelectedDiscipline?.id
            ) {
                setIsFetchingMoreElements(true)
                setIsNewDataLoading(true)
            } else {
                const firstElement = {
                    baseElementId:
                        categoryElementStates[newSelectedCategory?.id ?? '']
                            .data[0]?.baseElementId ?? '',
                    treePath:
                        categoryElementStates[newSelectedCategory?.id ?? '']
                            .data[0]?.treePath ?? '',
                    id:
                        categoryElementStates[newSelectedCategory?.id ?? '']
                            .data[0]?.id ??
                        categoryElementStates[newSelectedCategory?.id ?? '']
                            .data[0]?.elementId,
                }

                setSelectedElementIdentifier(firstElement)
            }
            // send serviceWorker change
            serviceWorker?.controller?.postMessage({
                [WORKSPACE_PRODUCT_LISTING_KEY]: {
                    category: newSelectedCategory,
                    projectId: projectId ?? '',
                    elementId,
                    nodeId,
                } as WorkspaceProductListing,
            })

            if (searchFilters) {
                setSearchFilters(undefined)
                setSkipDefaultQuery(true)
                persistState?.('searchFilters', undefined)
            }
        },
        [
            persistState,
            getPersistedState,
            categoryElementStates,
            serviceWorker,
            projectId,
            searchFilters,
            setSelectedElementIdentifier,
            selectedElementFilterType,
            selectedDiscipline,
        ]
    )

    const handleResetCategories = React.useCallback(() => {
        let categoyPanelDiv = document.getElementById(
            'categoryPanel'
        ) as HTMLElement
        handleSelectedCatgoryChange(
            {
                name: 'All Categories',
                id: 'all',
            },
            allDisciplineData[0]
        )
        if (categoyPanelDiv) {
            categoyPanelDiv.scrollTop = 0
        }
    }, [allDisciplineData])
    const adjustIndexerAfterElementRemove = React.useCallback(
        (categoryId: string, startIndex: number) => {
            treeIndexerRef.current.forEach((categoryIndexer) => {
                if (categoryIndexer.has(categoryId)) {
                    const elementIndex = categoryIndexer.get(categoryId) ?? -1
                    if (elementIndex > startIndex && elementIndex > 0) {
                        categoryIndexer.set(categoryId, elementIndex - 1)
                    }
                }
            })
        },
        [treeIndexerRef]
    )

    const getElementIndexInCategoryByTreePath = React.useCallback(
        (isUpdateAfterVersionChange?: boolean) => {
            if (!!selectedElementIdentifier?.treePath) {
                const dataToUpdate =
                    selectedElementFilterType?.type === 'DIVISIONS'
                        ? updatedDivisionElements(
                              categoryElementStates[
                                  selectedCategory?.id ?? 'all'
                              ].data,
                              categoryElementStates[
                                  selectedCategory?.id ?? 'all'
                              ].data
                          )
                        : categoryElementStates[selectedCategory?.id ?? 'all']
                              .data
                const selectedElementIndex = dataToUpdate.findIndex(
                    (element) => {
                        return selectedElementFilterType?.type ===
                            'DIVISIONS' && !isUpdateAfterVersionChange
                            ? element.id === selectedElementIdentifier?.id ||
                                  element?.baseElementId ===
                                      selectedElementIdentifier?.baseElementId
                            : element.treePath ===
                                  selectedElementIdentifier?.treePath
                    }
                )
                return selectedElementIndex ?? -1
            }
            return -1
        },
        [
            selectedCategory,
            selectedElementIdentifier,
            categoryElementStates,
            selectedElementFilterType,
        ]
    )
    const updateBPElementTreeChange = React.useCallback(
        (childElements: ProjectElementNode[]) => {
            if (BPElementTreePrams) {
                const newCategoryElementState: Record<
                    string,
                    CategoryElementListStateInfo
                > = JSON.parse(JSON.stringify(categoryElementStates))

                const elementList = getUpdateBpElementListByTreePath(
                    [
                        ...(newCategoryElementState[
                            selectedCategory?.id ?? 'all'
                        ]?.data ?? []),
                    ],
                    childElements ?? [],
                    BPElementTreePrams?.treepath,
                    BPElementTreePrams?.isBP,
                    BPElementTreePrams?.isGBP,
                    BPElementTreePrams?.remove
                )
                newCategoryElementState[
                    selectedCategory?.id ?? 'all'
                ].data = elementList

                setCategoryElementStates(newCategoryElementState)
                setBPElementTreePrams(null)
            }
        },
        [
            categoryElementStates,
            selectedCategory,
            BPElementTreePrams,
            setCategoryElementStates,
            setBPElementTreePrams,
        ]
    )

    const handleBPElementTreeChange = React.useCallback(
        (
            id: string,
            treepath: string,
            isBP: boolean,
            isGBP: boolean,
            remove: boolean
        ) => {
            setBPElementTreePrams({
                id: id,
                treepath: treepath,
                isBP: isBP,
                isGBP: isGBP,
                remove: remove,
            })
            if (!isBetaWorkspaceToggleEnabled) {
                //slightly scroll the tree for card which updated for Best Practice / Global Best Practice
                const variableSizedTreeDiv = window.document.getElementById(
                    'elementTreeViewContainer'
                )?.childNodes?.[0]?.childNodes?.[0] as HTMLDivElement

                if (!!variableSizedTreeDiv) {
                    variableSizedTreeDiv.scrollTop =
                        variableSizedTreeDiv.scrollTop + 1
                }
            }
        },
        [setBPElementTreePrams, isBetaWorkspaceToggleEnabled]
    )
    React.useEffect(() => {
        if (BPElementTreePrams) {
            const newCategoryElementState: Record<
                string,
                CategoryElementListStateInfo
            > = JSON.parse(JSON.stringify(categoryElementStates))
            if (
                BPElementTreePrams.remove &&
                isElementHasChildrenById(BPElementTreePrams.id, [
                    ...(newCategoryElementState[selectedCategory?.id ?? 'all']
                        ?.data ?? []),
                ])
            ) {
                getChildElementsList({
                    variables: {
                        input: {
                            projectId: projectId,
                            limit: MaxElementsPerBatch,
                            onlyElementsAssignedToProject: false,
                            projectElementType: Object.keys(
                                ProjectElementType
                            ) as ProjectElementType[],
                            parentNodeId: BPElementTreePrams.id,
                            skip: 0,
                            isFromAEWorkSpace: !contentTeamFromStudioPage,
                            isProductFamilyGroupView:
                                selectedElementFilterType?.type === 'DIVISIONS',
                        },
                    },
                })
            } else {
                updateBPElementTreeChange([] as ProjectElementNode[])
            }
        }
    }, [
        BPElementTreePrams,
        selectedElementFilterType,
        contentTeamFromStudioPage,
    ])

    /** Removes, appends, and/or replaces nodes in the workspace element tree */
    const updateElementList = React.useCallback(
        (
            /** Nodes to insert or replace in the tree */
            nodesToInsert: ProjectElementNode[],

            /** Parents of root node to insert/replace */
            parentElementNodes?: ProjectElementNode[],

            /** Nodes to delete from the tree (no replacement) */
            deletedElementNodes?: ProjectElementNode[],

            /** True if we're inserting a selection via the product selection dialog */
            appendElements: boolean = false,

            /** ID of element selected via product selection dialog */
            selectedElementFromDialog?: string,

            /** True if the update is triggered after a version change. */
            isUpdateAfterVersionChange: boolean = false
        ) => {
            const isDivisionSelected =
                selectedElementFilterType?.type === 'DIVISIONS'
            const newCategoryElementState: Record<
                string,
                CategoryElementListStateInfo
            > = JSON.parse(JSON.stringify(categoryElementStates))
            const selectedElementInProject = categoryElementStates[
                selectedCategory?.id ?? 'all'
            ]?.data?.filter(
                (item) =>
                    item?.baseElementId ===
                        selectedElementIdentifier?.baseElementId &&
                    item?.treePath === selectedElementIdentifier?.treePath
            )?.[0]
            let elementListToUpdate = [
                ...(isDivisionSelected
                    ? updatedDivisionElements(
                          newCategoryElementState[selectedCategory?.id ?? 'all']
                              ?.data,
                          newCategoryElementState[selectedCategory?.id ?? 'all']
                              ?.data
                      )
                    : newCategoryElementState[selectedCategory?.id ?? 'all']
                          ?.data ?? []),
            ]

            nodesToInsert = isDivisionSelected
                ? updatedDivisionElements(
                      nodesToInsert,
                      newCategoryElementState[selectedCategory?.id ?? 'all']
                          ?.data
                  )
                : nodesToInsert

            const isProductFamilyNameChanged = checkProductFamilyNameChanged(
                elementListToUpdate,
                nodesToInsert,
                selectedElementFilterType?.type
            )

            const areDeletingNodes: boolean =
                Array.isArray(deletedElementNodes) &&
                deletedElementNodes.length > 0
            const areInsertingOrReplacingNodes: boolean =
                Array.isArray(nodesToInsert) && nodesToInsert.length > 0
            if (areDeletingNodes) {
                // The last instance(s) of user-added elements, such as PT copies, were removed;
                // remove their nodes from the tree.
                const removeFirstNodeResult = removeElementNodeFromList(
                    deletedElementNodes![0],
                    elementListToUpdate,
                    isDivisionSelected
                )
                const nodeToSelect = removeFirstNodeResult.nodeToSelect
                elementListToUpdate = removeFirstNodeResult.elementList

                // Remove any additional nodes (e.g., other instances of the PT copy)
                let removeNodeResult: RemovedElementResult
                if (deletedElementNodes!.length >= 2) {
                    deletedElementNodes!.slice(1).forEach((el) => {
                        removeNodeResult = removeElementNodeFromList(
                            el,
                            elementListToUpdate,
                            isDivisionSelected
                        )
                        elementListToUpdate = removeNodeResult.elementList
                    })
                }

                // If we aren't doing additional work on the element tree such as replacing nodes,
                // go ahead and update the tree now.
                if (!areInsertingOrReplacingNodes) {
                    newCategoryElementState[selectedCategory?.id ?? 'all'] = {
                        ...newCategoryElementState[
                            selectedCategory?.id ?? 'all'
                        ],
                        data: isDivisionSelected
                            ? updatedDivisionElements(
                                  elementListToUpdate,
                                  categoryElementStates[
                                      selectedCategory?.id ?? 'all'
                                  ]?.data
                              )
                            : elementListToUpdate,
                        totalElementCount: elementListToUpdate.length,
                    }
                    setCategoryElementStates(newCategoryElementState)

                    // Select the removed element's next sibling
                    if (nodeToSelect) {
                        setSelectedElementIdentifier({
                            baseElementId: nodeToSelect.baseElementId,
                            treePath: nodeToSelect.treePath,
                            id: nodeToSelect.id ?? nodeToSelect?.elementId,
                        })
                    }
                }
            }

            const existingSelectedElement = selectedElementIdentifier
            //keep current element index so that it can be used to determine new selection
            const existingSelectedElementIndex = getElementIndexInCategoryByTreePath(
                isUpdateAfterVersionChange
            )

            if (isUpdateAfterVersionChange) {
                let newElementListToUpdate: ProjectElementNode[] = []

                const nodesToInsertMap = new Map<string, ProjectElementNode[]>()
                nodesToInsert?.forEach((node) => {
                    // condition to check if the returned node is added to project
                    if (
                        (selectedElementFilterType?.value ===
                            ADDED_TO_PROJECT ||
                            selectedElementFilterType?.value ===
                                'Added to Work Results') &&
                        !node?.isInProject
                    ) {
                        return null
                    }

                    if (node.elementType === ElementType.PRODUCTTYPE) {
                        if (nodesToInsertMap.has(node['parentTreePath'])) {
                            nodesToInsertMap
                                ?.get(node['parentTreePath'])
                                ?.push(node)
                        } else {
                            nodesToInsertMap.set(node['parentTreePath'], [node])
                        }
                    } else {
                        nodesToInsertMap.set(node['treePath'], [node])
                    }
                })

                const familyUpdated = nodesToInsert.find(
                    (element) =>
                        element?.elementType === ElementType.PRODUCTFAMILY
                )

                elementListToUpdate = elementListToUpdate?.filter(
                    (element) =>
                        !element.parentTreePath?.includes(familyUpdated?.text!)
                )

                elementListToUpdate?.forEach((element) => {
                    if (nodesToInsertMap?.has(element['treePath'])) {
                        newElementListToUpdate.push(
                            ...nodesToInsertMap?.get(element['treePath'])!
                        )
                    } else {
                        newElementListToUpdate.push(element)
                    }
                })

                elementListToUpdate = [...newElementListToUpdate]

                newCategoryElementState[selectedCategory?.id ?? 'all'] = {
                    ...newCategoryElementState[selectedCategory?.id ?? 'all'],
                    data: elementListToUpdate,
                    totalElementCount: elementListToUpdate.length,
                }

                setCategoryElementStates(newCategoryElementState)
                setSelectedElementIdentifier({
                    treePath:
                        elementListToUpdate[existingSelectedElementIndex]
                            .treePath,
                    baseElementId:
                        elementListToUpdate[existingSelectedElementIndex]
                            .baseElementId,
                    id:
                        elementListToUpdate[existingSelectedElementIndex]?.id ??
                        elementListToUpdate[existingSelectedElementIndex]
                            ?.elementId,
                })
            } else if (
                areInsertingOrReplacingNodes &&
                onlyElementsAssignedToProjectFilter
            ) {
                let newElementList = isDivisionSelected
                    ? updatedDivisionElements(
                          [...nodesToInsert],
                          categoryElementStates[selectedCategory?.id ?? 'all']
                              ?.data
                      )
                    : [...nodesToInsert]

                nodesToInsert.forEach((each) => {
                    const updatedElementIndex = getElementIndexInTreePath(
                        elementListToUpdate,
                        each,
                        isDivisionSelected
                    )
                    if (updatedElementIndex > -1) {
                        if (appendElements) {
                            // replace old element with the updated element
                            const newElementIndex = newElementList.findIndex(
                                (element) =>
                                    element.treePath === each.treePath ||
                                    element.id === each.id
                            )

                            const updatedElement = newElementList.slice(
                                newElementIndex,
                                newElementIndex + 1
                            )

                            elementListToUpdate = elementListToUpdate
                                .slice(0, updatedElementIndex)
                                ?.concat(updatedElement)
                                ?.concat(
                                    elementListToUpdate.slice(
                                        updatedElementIndex + 1
                                    )
                                )

                            newElementList = newElementList
                                .slice(0, newElementIndex)
                                ?.concat(
                                    newElementList.slice(newElementIndex + 1)
                                )
                        } else if (!each.isInProject) {
                            const dataInProject: ProjectElementNode[] = isDivisionSelected
                                ? updatedDivisionElements(
                                      elementListToUpdate,
                                      categoryElementStates?.[
                                          selectedCategory?.id ?? 'all'
                                      ]?.data
                                  )
                                : elementListToUpdate
                            // Assigned To Project Filter is applied and we get isInProject as false we are remove that element from the current list
                            const removeElementIndex = dataInProject.findIndex(
                                (element) =>
                                    element.treePath === each.treePath ||
                                    element.id === each.id
                            )
                            if (removeElementIndex > -1) {
                                elementListToUpdate = elementListToUpdate
                                    .slice(0, removeElementIndex)
                                    .concat(
                                        elementListToUpdate.slice(
                                            removeElementIndex + 1
                                        )
                                    )
                            }
                        } else {
                            // remove the old element from the current list
                            const childParentNode =
                                elementListToUpdate[updatedElementIndex]
                            if (
                                !(contentTeamFromStudioPage && ADDED_TO_DRAFT)
                            ) {
                                elementListToUpdate = elementListToUpdate
                                    .slice(0, updatedElementIndex)
                                    ?.concat(
                                        elementListToUpdate.slice(
                                            updatedElementIndex + 1
                                        )
                                    )
                            }

                            // remove childs node if Product Family Name is changed
                            if (
                                isProductFamilyNameChanged &&
                                each.elementType === 'PRODUCTFAMILY' &&
                                childParentNode.elementType === 'PRODUCTFAMILY'
                            ) {
                                elementListToUpdate = elementListToUpdate.filter(
                                    (child) =>
                                        child.parentNodeId !==
                                        childParentNode.id
                                )
                            }
                            // adjust index in all categroy where this element is being removed
                            adjustIndexerAfterElementRemove(
                                selectedCategory?.id ?? 'all',
                                updatedElementIndex
                            )
                        }

                        newCategoryElementState[
                            selectedCategory?.id ?? 'all'
                        ] = {
                            ...newCategoryElementState[
                                selectedCategory?.id ?? 'all'
                            ],
                            data: elementListToUpdate,
                            totalElementCount: elementListToUpdate.length,
                        }
                    }
                })

                if (parentElementNodes && parentElementNodes.length > 0) {
                    parentElementNodes.forEach((parentElementNode) => {
                        const parentElementNodeIndex = elementListToUpdate.findIndex(
                            (element) =>
                                element.treePath ===
                                    parentElementNode?.treePath ||
                                element.id === parentElementNode.id
                        )
                        if (parentElementNodeIndex > -1) {
                            elementListToUpdate[
                                parentElementNodeIndex
                            ] = parentElementNode
                            newCategoryElementState[
                                selectedCategory?.id ?? 'all'
                            ] = {
                                ...newCategoryElementState[
                                    selectedCategory?.id ?? 'all'
                                ],
                                data:
                                    isDivisionSelected &&
                                    selectedElementFilterType?.value ===
                                        ADDED_TO_PROJECT &&
                                    !parentElementNode?.isInProject
                                        ? elementListToUpdate?.filter(
                                              (itemNew, indexNew) => {
                                                  return (
                                                      indexNew !==
                                                      parentElementNodeIndex
                                                  )
                                              }
                                          )
                                        : elementListToUpdate,
                                totalElementCount: elementListToUpdate.length,
                            }
                        }
                    })
                }

                if (appendElements) {
                    const insertResult = insertNewElements(
                        newElementList,
                        elementListToUpdate,
                        nodesToInsert.length
                    )

                    // If insertNewElements returned null, parent not found - trigger refetch
                    if (insertResult === null) {
                        setFetchAllElements(true)
                        return
                    }

                    elementListToUpdate = insertResult

                    newCategoryElementState[selectedCategory?.id ?? 'all'] = {
                        ...newCategoryElementState[
                            selectedCategory?.id ?? 'all'
                        ],
                        data: elementListToUpdate,
                        totalElementCount: elementListToUpdate.length,
                    }
                }
                if (selectedElementFromDialog) {
                    const selectCardIndex = nodesToInsert?.findIndex(
                        (item) => item?.elementId === selectedElementFromDialog
                    )

                    insertNewProductTypeElement(
                        nodesToInsert?.[selectCardIndex],
                        true
                    )
                } else {
                    setCategoryElementStates(newCategoryElementState)

                    if (isUpdateAfterVersionChange) {
                        setSelectedElementIdentifier({
                            treePath:
                                elementListToUpdate[
                                    existingSelectedElementIndex
                                ].treePath,
                            baseElementId:
                                elementListToUpdate[
                                    existingSelectedElementIndex
                                ].baseElementId,
                            id:
                                elementListToUpdate[
                                    existingSelectedElementIndex
                                ]?.id ??
                                elementListToUpdate[
                                    existingSelectedElementIndex
                                ]?.elementId,
                        })
                    } else {
                        setNextSelectableElement(
                            newCategoryElementState[
                                selectedCategory?.id ?? 'all'
                            ].data,
                            existingSelectedElementIndex
                        )
                    }
                }
            } else if (areInsertingOrReplacingNodes) {
                const productFamilyList = nodesToInsert.filter(
                    (each) => each.elementType === 'PRODUCTFAMILY'
                )
                const haveProductTypes =
                    nodesToInsert.filter(
                        (each) => each.elementType === 'PRODUCTTYPE'
                    )?.length == 0 &&
                    selectedElementInProject?.isLastSubAssembly

                const productTypeList = haveProductTypes
                    ? elementListToUpdate?.filter(
                          (item) =>
                              item?.elementType === 'PRODUCTTYPE' &&
                              item?.treePath?.includes(
                                  selectedElementInProject?.treePath
                              )
                      )
                    : nodesToInsert.filter(
                          (each) => each.elementType === 'PRODUCTTYPE'
                      )
                const assemblyList = nodesToInsert.filter(
                    (each) => each.elementType === 'ASSEMBLY'
                )
                const assemblyListParentElementNodes = parentElementNodes?.filter(
                    (each) => each.elementType === 'ASSEMBLY'
                )

                if (productFamilyList.length > 0) {
                    productFamilyList.forEach((each) => {
                        const updatedProductFamilyElementIndex = getElementIndexInTreePath(
                            elementListToUpdate,
                            each,
                            isDivisionSelected
                        )

                        if (updatedProductFamilyElementIndex > -1) {
                            const oldProductFamilyId =
                                elementListToUpdate[
                                    updatedProductFamilyElementIndex
                                ].id

                            elementListToUpdate[
                                updatedProductFamilyElementIndex
                            ] = {
                                ...each,
                                isSearchResult:
                                    elementListToUpdate[
                                        updatedProductFamilyElementIndex
                                    ].isSearchResult,
                                parentNodeId:
                                    elementListToUpdate[
                                        updatedProductFamilyElementIndex
                                    ].parentNodeId,
                            }

                            let newProductTypeList = (selectedElementInProject?.isLastSubAssembly
                                ? productTypeList
                                : nodesToInsert
                            ).filter(
                                (element) =>
                                    element.parentNodeId === each.id ||
                                    element.parentTreePath === each.treePath
                            )

                            if (
                                oldProductFamilyId === each.id &&
                                assemblyList.length === 0 &&
                                (assemblyListParentElementNodes ?? [])
                                    .length === 0
                            ) {
                                elementListToUpdate = replaceElementsList(
                                    elementListToUpdate,
                                    newProductTypeList,
                                    isDivisionSelected
                                )
                            } else {
                                const newProductTypeIds = new Set(
                                    newProductTypeList.map((item) => item.id)
                                )
                                const newProductTypeTreePaths = new Set(
                                    newProductTypeList.map(
                                        (item) => item.treePath
                                    )
                                )
                                // Remove non-ProductType children of old ProductFamily and ProductTypes being replaced
                                const shouldKeepElement = (
                                    element: ProjectElementNode
                                ) => {
                                    const isChildOfOldFamily =
                                        element.parentNodeId ===
                                        oldProductFamilyId

                                    if (!isChildOfOldFamily) return true

                                    const isProductType =
                                        element.elementType === 'PRODUCTTYPE'
                                    const isBeingReplaced = isDivisionSelected
                                        ? newProductTypeTreePaths.has(
                                              element.treePath
                                          )
                                        : newProductTypeIds.has(element.id)

                                    return isProductType && !isBeingReplaced
                                }

                                elementListToUpdate = elementListToUpdate.filter(
                                    shouldKeepElement
                                )

                                /**
                                 * CRITICAL FIX: Parent-Child Relationship Maintenance
                                 *
                                 * When a family is added to a project, its ID changes from catalog ID to project ID.
                                 * If the family was already expanded (children loaded with catalog parentNodeId),
                                 * those children become orphaned unless we update their parentNodeId to match the
                                 * new family project ID. This ensures children remain connected to their parent.
                                 */
                                if (oldProductFamilyId !== each.id) {
                                    elementListToUpdate = elementListToUpdate.map(
                                        (element) =>
                                            element.parentNodeId ===
                                            oldProductFamilyId
                                                ? {
                                                      ...element,
                                                      parentNodeId: each.id,
                                                  }
                                                : element
                                    )
                                }

                                if (searchFilters && searchFilters.length > 0) {
                                    const productFamilySearchApplied = searchFilters.find(
                                        (filter) =>
                                            filter.projectElementType ===
                                            'PRODUCTFAMILY'
                                    )
                                    const productTypeSearchApplied = searchFilters.find(
                                        (filter) =>
                                            filter.projectElementType ===
                                            'PRODUCTTYPE'
                                    )

                                    newProductTypeList = newProductTypeList.map(
                                        (element) => ({
                                            ...element,
                                            isSearchResult: productTypeSearchApplied
                                                ? element.text
                                                      .toLowerCase()
                                                      .includes(
                                                          productTypeSearchApplied.searchText.toLowerCase()
                                                      )
                                                : false,
                                        })
                                    )
                                    if (
                                        !(
                                            productFamilySearchApplied &&
                                            each.text
                                                .toLowerCase()
                                                .includes(
                                                    productFamilySearchApplied.searchText.toLowerCase()
                                                )
                                        )
                                    ) {
                                        newProductTypeList = newProductTypeList.filter(
                                            (element) => element.isSearchResult
                                        )
                                    }
                                }
                                //combine New Product Type using Family Index of current family
                                const updatedElementsList = [
                                    ...elementListToUpdate.slice(
                                        0,
                                        updatedProductFamilyElementIndex + 1
                                    ),
                                    ...newProductTypeList,
                                    ...elementListToUpdate.slice(
                                        updatedProductFamilyElementIndex + 1
                                    ),
                                ]
                                //sort updated elements in the same order as it exists before add/remove
                                elementListToUpdate = sortProjectElementsNodes(
                                    newCategoryElementState[
                                        selectedCategory?.id ?? 'all'
                                    ].data,
                                    updatedElementsList
                                )
                            }
                        }
                    })
                }

                // removing duplicate elements
                elementListToUpdate = Array.from(
                    new Map(
                        elementListToUpdate.map((item) => [item.treePath, item])
                    ).values()
                )

                if (productTypeList.length > 0) {
                    elementListToUpdate = replaceElementsList(
                        elementListToUpdate,
                        productTypeList,
                        isDivisionSelected
                    )
                }
                if (
                    selectedElementFilterType?.type === 'DIVISIONS' &&
                    assemblyListParentElementNodes &&
                    assemblyListParentElementNodes?.length !== 0
                ) {
                    elementListToUpdate = replaceElementsList(
                        elementListToUpdate,
                        assemblyListParentElementNodes,
                        isDivisionSelected
                    )
                }
                if (assemblyList.length > 0) {
                    elementListToUpdate = replaceElementsList(
                        elementListToUpdate,
                        assemblyList,
                        isDivisionSelected
                    )
                }
                const updatedSelectProduct =
                    selectedProjectElementNode?.elementType === 'PRODUCTTYPE'
                        ? nodesToInsert?.filter(
                              (itemF) =>
                                  itemF?.treePath ===
                                  selectedProjectElementNode?.treePath
                          )?.[0]
                        : undefined
                newCategoryElementState[
                    selectedCategory?.id ?? 'all'
                ].data = isDivisionSelected
                    ? updatedDivisionElements(
                          elementListToUpdate,
                          categoryElementStates[selectedCategory?.id ?? 'all']
                              .data
                      )
                    : elementListToUpdate?.map((item) => {
                          if (
                              item?.treePath ===
                                  selectedProjectElementNode?.treePath &&
                              updatedSelectProduct
                          ) {
                              return updatedSelectProduct
                          } else return item
                      })

                setCategoryElementStates(newCategoryElementState)

                setSelectedElementIdentifier(
                    isProductFamilyNameChanged
                        ? {
                              treePath:
                                  elementListToUpdate[
                                      existingSelectedElementIndex
                                  ].treePath,
                              baseElementId:
                                  elementListToUpdate[
                                      existingSelectedElementIndex
                                  ].baseElementId,
                              id:
                                  elementListToUpdate[
                                      existingSelectedElementIndex
                                  ]?.id ??
                                  elementListToUpdate[
                                      existingSelectedElementIndex
                                  ]?.elementId,
                          }
                        : existingSelectedElement
                )
                // persist the selected tree element
                persistState?.(
                    selectedElementFilterType?.type === 'DIVISIONS'
                        ? PERSISTED_ELEMENT_DIVISIONS
                        : PERSISTED_ELEMENT_ASSEMBLIES,
                    isProductFamilyNameChanged ||
                        elementListToUpdate[existingSelectedElementIndex]
                            ?.id !== existingSelectedElement?.id
                        ? {
                              treePath:
                                  elementListToUpdate[
                                      existingSelectedElementIndex
                                  ].treePath,
                              baseElementId:
                                  elementListToUpdate[
                                      existingSelectedElementIndex
                                  ].baseElementId,
                              id:
                                  elementListToUpdate[
                                      existingSelectedElementIndex
                                  ]?.id ??
                                  elementListToUpdate[
                                      existingSelectedElementIndex
                                  ]?.elementId,
                          }
                        : existingSelectedElement
                )
            }

            setTimeout(() => {
                refetchAddedElementsCount && refetchAddedElementsCount()
                refetchSelectedProductsCount()
                resetElementAfterBulkOperation()
            }, 1000)
        },
        [
            searchFilters,
            categoryElementStates,
            selectedCategory,
            onlyElementsAssignedToProjectFilter,
            selectedElementFilterType,
            selectedElementIdentifier,
            adjustIndexerAfterElementRemove,
            getElementIndexInCategoryByTreePath,
            setCategoryElementStates,
            setNextSelectableElement,
            refetchSelectedProductsCount,
            setSelectedElementIdentifier,
            contentTeamFromStudioPage,
            ADDED_TO_DRAFT,
            persistState,
            resetElementAfterBulkOperation,
        ]
    )

    const insertNewProductTypeElement = React.useCallback(
        (
            element: ProjectElementNode,
            isSelectedFromListingDialog?: boolean,
            newNodesForAdditionalInstances?: ProjectElementNode[],
            isAddingNewPT?: boolean,
            allNodes?: ProjectElementNode[]
        ) => {
            // Clone current element state so we don't modify it
            const newCategoryElementState: Record<
                string,
                CategoryElementListStateInfo
            > = JSON.parse(JSON.stringify(categoryElementStates))

            // From the copy, get the element list for the current category selection
            let elementList = [
                ...(newCategoryElementState[selectedCategory?.id ?? 'all']
                    ?.data ?? []),
            ]

            const elementListLengthBeforeInsert = elementList?.length

            // Reconcile family node ID mismatch: the mutation response assigns a new project-specific
            // id to the family when the first PT is added under it. The workspace flat list still holds
            // the old catalog id for that family. `insertProductTypeElement` matches siblings by
            // parentNodeId — if the ids differ, zero siblings are found and insertion silently fails.
            // Fix: find the family in the flat list by treePath (stable text path), mark it
            // isInProject, and patch the PT's parentNodeId to match the existing family id so
            // sibling lookup succeeds.
            let insertableElement = element
            // Track family id promotion to apply AFTER ancestor traversal (traversal uses the old id)
            let pendingFamilyIdPromotion: {
                idx: number
                catalogId: string
                projectId: string
                projectElementId: string | null
                treePath: string
                baseElementId: string
            } | null = null

            // In Divisions view, treePaths in the flat list have been remapped by
            // updatedDivisionElements (e.g. "A1010.1010|Continuous Footings||Family||PT"
            // instead of the full Assembly path). Remap allNodes to Division-format treePaths
            // so the family and PT treePath lookups below can match what's in the flat list.
            const isDivisionView =
                selectedElementFilterType?.type === 'DIVISIONS'
            const nodesForMatch =
                isDivisionView && allNodes
                    ? updatedDivisionElements(allNodes, elementList)
                    : allNodes
            if (isDivisionView && nodesForMatch) {
                const remappedPT = nodesForMatch.find(
                    (n) => n.id === element.id
                )
                if (remappedPT) {
                    insertableElement = remappedPT
                }
            }

            if (allNodes && element.parentNodeId) {
                const familyFromResponse = nodesForMatch?.find(
                    (n) =>
                        n.id === element.parentNodeId &&
                        n.elementType === 'PRODUCTFAMILY'
                )
                if (familyFromResponse?.treePath) {
                    let existingFamilyIdx = elementList.findIndex(
                        (n) => n.treePath === familyFromResponse.treePath
                    )
                    // Divisions-view fallback: the mutation response's hierarchy uses
                    // Uniformat ancestors (e.g., Z4010.2030|...) while elementList in
                    // Divisions view uses CSI ancestors (e.g., 09|FINISHES). The treePath
                    // rebuilt by updatedDivisionElements above stays Uniformat-shaped
                    // because the response's own grandparent is the Uniformat assembly.
                    // Match by family text (CSI section code, e.g. "092900|GYPSUM BOARD")
                    // which is stable across views. Without this, the family lookup misses,
                    // ancestor traversal can't walk up, and the new PT is never inserted.
                    if (existingFamilyIdx === -1 && isDivisionView) {
                        existingFamilyIdx = elementList.findIndex(
                            (n) =>
                                n.elementType === 'PRODUCTFAMILY' &&
                                n.text === familyFromResponse.text
                        )
                    }
                    if (existingFamilyIdx !== -1) {
                        const existingFamily = elementList[existingFamilyIdx]
                        // Mark family as isInProject but keep the catalog id for now so the
                        // ancestor traversal below can still find it by id. We'll promote the
                        // id to the project UUID after traversal completes.
                        elementList = [
                            ...elementList.slice(0, existingFamilyIdx),
                            { ...existingFamily, isInProject: true },
                            ...elementList.slice(existingFamilyIdx + 1),
                        ]
                        // If the ids differ, patch the PT so sibling lookup matches the existing family
                        if (existingFamily.id !== familyFromResponse.id) {
                            insertableElement = {
                                ...insertableElement,
                                parentNodeId: existingFamily.id,
                            }
                        }
                        // Record the id promotion to apply post-traversal
                        pendingFamilyIdPromotion = {
                            idx: existingFamilyIdx,
                            catalogId: existingFamily.id,
                            projectId: familyFromResponse.id,
                            projectElementId: familyFromResponse.elementId,
                            treePath: familyFromResponse.treePath,
                            baseElementId: existingFamily.baseElementId,
                        }
                    }
                }
            }

            // If the family was expanded before add, a catalog node with the same treePath
            // already exists in the flat list (isInProject: false). Update it in-place
            // instead of inserting a duplicate project node alongside it.
            let existingPTIdx = elementList.findIndex(
                (n) =>
                    n.treePath === insertableElement.treePath &&
                    n.elementType === 'PRODUCTTYPE'
            )
            // Divisions-view fallback (mirrors the family lookup fallback above):
            // when the response's Uniformat treePath doesn't match the Division-format
            // treePath in elementList, match by (parentNodeId from the matched family)
            // + PT text. insertableElement.parentNodeId was already patched above to the
            // existing family's id when the family was matched, so this lookup finds the
            // PT among that family's children.
            if (existingPTIdx === -1 && isDivisionView) {
                existingPTIdx = elementList.findIndex(
                    (n) =>
                        n.elementType === 'PRODUCTTYPE' &&
                        n.parentNodeId === insertableElement.parentNodeId &&
                        n.text === insertableElement.text
                )
            }
            // Track the catalog PT id so we can update persisted selection if the user is on this PT
            const existingPTCatalogId =
                existingPTIdx !== -1 ? elementList[existingPTIdx].id : null
            if (existingPTIdx !== -1) {
                elementList = [
                    ...elementList.slice(0, existingPTIdx),
                    {
                        ...elementList[existingPTIdx],
                        isInProject: true,
                        // Promote to project UUIDs so that persistedElementNodeId on refresh
                        // sends the project UUID and the backend returns isInProject: true nodes
                        id: insertableElement.id,
                        elementId: insertableElement.elementId,
                    },
                    ...elementList.slice(existingPTIdx + 1),
                ]
            } else {
                // Family not yet expanded — insert the new project node at the correct position
                elementList = insertProductTypeElement(
                    insertableElement,
                    elementList
                )

                // Fallback: if insertProductTypeElement found no siblings (family is in the flat list
                // but its PT children haven't been loaded yet), insert the PT directly after the family
                if (
                    !elementList.some((n) => n.id === insertableElement.id) &&
                    insertableElement.parentNodeId
                ) {
                    const familyFallbackIdx = elementList.findIndex(
                        (n) => n.id === insertableElement.parentNodeId
                    )
                    if (familyFallbackIdx !== -1) {
                        elementList = [
                            ...elementList.slice(0, familyFallbackIdx),
                            {
                                ...elementList[familyFallbackIdx],
                                hasChildren: true,
                            },
                            insertableElement,
                            ...elementList.slice(familyFallbackIdx + 1),
                        ]
                    }
                }
            }

            // Mark remaining ancestors (assembly and above) as isInProject for the green highlight.
            // This traversal uses insertableElement.parentNodeId which still points to the catalog
            // family id — intentional: we haven't promoted the family's id yet.
            let currentParentNodeId = insertableElement.parentNodeId
            while (currentParentNodeId) {
                const ancestorIndex = elementList.findIndex(
                    (n) => n.id === currentParentNodeId
                )
                if (ancestorIndex === -1) break
                const ancestor = elementList[ancestorIndex]
                if (!ancestor.isInProject) {
                    elementList = [
                        ...elementList.slice(0, ancestorIndex),
                        { ...ancestor, isInProject: true },
                        ...elementList.slice(ancestorIndex + 1),
                    ]
                }
                currentParentNodeId = ancestor.parentNodeId
            }

            // Now promote the family's id/elementId to the project UUIDs. This must happen after
            // the ancestor traversal because the traversal locates the family by its catalog id.
            // Promoting before traversal would cause the family lookup to miss, stopping the walk
            // before assembly ancestors get their isInProject flag set.
            if (pendingFamilyIdPromotion !== null) {
                const promotion = pendingFamilyIdPromotion
                elementList = [
                    ...elementList.slice(0, promotion.idx),
                    {
                        ...elementList[promotion.idx],
                        id: promotion.projectId,
                        elementId: promotion.projectElementId,
                    },
                    ...elementList.slice(promotion.idx + 1),
                ]
                // Repoint any child nodes that referenced the stale catalog family id
                // to the new project family id. ElementsPanel re-runs
                // updatedDivisionElements(elementFlatList) without a fallback whenever
                // the flat list changes; a stale parentNodeId would make the parent
                // lookup miss and produce "undefined||undefined||..." treePaths,
                // orphaning the PT in the tree (no green isInProject highlight, wrong
                // tree position). Both the newly inserted/promoted PT and any
                // pre-existing catalog PT siblings under the old catalog family id
                // need this repoint.
                elementList = elementList.map((n) =>
                    n.parentNodeId === promotion.catalogId
                        ? { ...n, parentNodeId: promotion.projectId }
                        : n
                )
                // If the user is currently on this family node, update the persisted selection
                // so that on refresh persistedElementNodeId carries the project UUID.
                if (selectedProjectElementNode?.id === promotion.catalogId) {
                    const updatedIdentifier = {
                        baseElementId: promotion.baseElementId,
                        treePath: promotion.treePath,
                        id: promotion.projectId,
                    }
                    setSelectedElementIdentifier(updatedIdentifier)
                    persistState?.(
                        selectedElementFilterType?.type === 'DIVISIONS'
                            ? PERSISTED_ELEMENT_DIVISIONS
                            : PERSISTED_ELEMENT_ASSEMBLIES,
                        updatedIdentifier
                    )
                }
            }

            // If the user is currently on the PT that was just promoted from catalog to project,
            // update the persisted selection to use the project UUID.
            if (
                existingPTCatalogId !== null &&
                selectedProjectElementNode?.id === existingPTCatalogId
            ) {
                const updatedPTIdentifier = {
                    baseElementId: insertableElement.baseElementId,
                    treePath: insertableElement.treePath,
                    id: insertableElement.id,
                }
                setSelectedElementIdentifier(updatedPTIdentifier)
                persistState?.(
                    selectedElementFilterType?.type === 'DIVISIONS'
                        ? PERSISTED_ELEMENT_DIVISIONS
                        : PERSISTED_ELEMENT_ASSEMBLIES,
                    updatedPTIdentifier
                )
            }

            const elementListLengthAfterInsert = elementList?.length

            // Add the nodes for the additional instances of the element
            // (except ones that are filtered out by our current tree filters)
            if (
                newNodesForAdditionalInstances &&
                newNodesForAdditionalInstances.length > 0
            ) {
                if (
                    // If the function is called while adding new PT and if there is no changes in element list then add element from the `newNodesForAdditionalInstances`
                    isAddingNewPT &&
                    elementListLengthBeforeInsert ===
                        elementListLengthAfterInsert
                ) {
                    newNodesForAdditionalInstances
                        .filter(
                            (newInstanceNode) =>
                                !onlyElementsAssignedToProjectFilter ||
                                newInstanceNode.isInProject
                        )
                        .forEach((newInstanceNode) => {
                            elementList = insertProductTypeElement(
                                newInstanceNode,
                                elementList,
                                true
                            )
                        })
                }
            }

            // Update the element state with our changes
            newCategoryElementState[selectedCategory?.id ?? 'all'] = {
                ...newCategoryElementState[selectedCategory?.id ?? 'all'],
                data: elementList,
                totalElementCount: elementList.length,
            }

            setCategoryElementStates(newCategoryElementState)

            // If the node element is not added, then select the first element from 'newNodesForAdditionalInstances'
            const currElement =
                elementListLengthBeforeInsert ===
                    elementListLengthAfterInsert &&
                newNodesForAdditionalInstances &&
                newNodesForAdditionalInstances.length > 0
                    ? newNodesForAdditionalInstances[0]
                    : element

            // Select the new PT element in the tree and persist so page refresh restores it
            // Exceptions:
            //   1. If the user is currently on a PRODUCTFAMILY node, stay on it — do not switch to the new PT
            //   2. If this insert came from a SW WORKSPACE_TREE_ELEMENT_NODES message (allNodes defined),
            //      skip auto-navigation — selecting the freshly-created PT triggers getProjectProductFamilyView
            //      with the new PT's elementId, which the backend rejects as "does not exist".
            //      The green highlight (isInProject: true) is enough feedback for the background update.
            if (
                !isSelectedFromListingDialog &&
                !allNodes &&
                selectedProjectElementNode?.elementType !== 'PRODUCTFAMILY'
            ) {
                const elementIdentifier = {
                    baseElementId: currElement.baseElementId,
                    treePath: currElement.treePath,
                    id: currElement.id,
                }
                setSelectedElementIdentifier(elementIdentifier)
                persistState?.(
                    selectedElementFilterType?.type === 'DIVISIONS'
                        ? PERSISTED_ELEMENT_DIVISIONS
                        : PERSISTED_ELEMENT_ASSEMBLIES,
                    elementIdentifier
                )
            }

            // Update "Added to Project" element count
            setTimeout(() => {
                refetchAddedElementsCount && refetchAddedElementsCount()
                refetchSelectedProductsCount()
            }, 1000)
        },
        [
            searchFilters,
            setCategoryElementStates,
            selectedCategory,
            categoryElementStates,
            refetchAddedElementsCount,
            refetchSelectedProductsCount,
            setSelectedElementIdentifier,
            persistState,
            selectedElementFilterType,
            selectedProjectElementNode,
        ]
    )

    const updateLocalStorageElementValues = React.useCallback(
        (propKey: string, inputValue: boolean | any) => {
            saveToLocalStorage(propKey, inputValue, currentUserId, projectId)
        },
        [currentUserId, projectId]
    )

    // inserts new elements after its sibling in the original elementList
    const insertNewElements = (
        newElementList: ProjectElementNode[],
        currentElementList: ProjectElementNode[],
        newElementCount: number
    ): ProjectElementNode[] | null => {
        while (newElementList.length > 0) {
            const parentId = newElementList[0].parentNodeId
            const siblingElements = newElementList.filter(
                (element) => element.parentNodeId === parentId
            )

            const childElements = currentElementList.filter(
                (element) => element.parentNodeId === parentId
            )

            const indexToInsert =
                childElements.length > 0
                    ? currentElementList.lastIndexOf(
                          childElements[childElements.length - 1]
                      )
                    : currentElementList.findIndex(
                          (element) => element.id === parentId
                      )

            // If parent not found in current tree, return null to signal refetch needed
            if (indexToInsert === -1) {
                return null
            }

            currentElementList = currentElementList
                .slice(0, indexToInsert + 1)
                .concat(siblingElements)
                .concat(currentElementList.slice(indexToInsert + 1))

            newElementList = newElementList.slice(
                siblingElements.length,
                newElementCount
            )
        }
        return currentElementList
    }

    const selectedProductsCaption = React.useMemo(() => {
        const countFromQuery =
            selectedProductsCount?.getProjectManufacturerProductListing
                ?.length ?? 0
        const countFromState = projectProductListingCount
        // Network status 7 = in Apollo, query was initially served from the cache so no network call is initiated
        // While the data is available, the system is waiting for a potential network request to complete, and the loading state may remain true
        const networkReady = selectedProductsCountNetworkStatus >= 7

        // Use state count as fallback when network is not ready to prevent flickering
        return `${networkReady ? countFromQuery : countFromState} items`
    }, [
        selectedProductsCount,
        projectProductListingCount,
        selectedProductsCountNetworkStatus,
    ])

    const leftWidgets = React.useMemo(
        (): WorkspaceActionBarWidget[][] => [
            [
                ...(!contentTeamFromStudioPage
                    ? [
                          {
                              label: 'Project Parameters',
                              component: (
                                  <ProjectParametersDialog
                                      projectId={projectId}
                                  />
                              ),
                          },
                      ]
                    : []),
            ],
            [
                {
                    label: 'Product Selection',
                    component: (
                        <WorkspaceActionBarButtonWidget
                            onClick={handleViewAllProducts}
                            className="btnViewAllProducts"
                            disabled={
                                projectLoading || areElementsLoadingOrNotReady
                            }
                            label={'Product Selection'}
                        >
                            View Products
                        </WorkspaceActionBarButtonWidget>
                    ),
                },

                ...(!contentTeamFromStudioPage
                    ? [
                          {
                              label: {
                                  text: 'Selected Products',
                              },
                              component: (
                                  <WorkspaceActionBarIconTextWidget
                                      iconColor={'#FFFFFF'}
                                      disabled={areElementsLoadingOrNotReady}
                                      icon={LoyaltyOutlinedIcon}
                                      caption={selectedProductsCaption}
                                      hasEllipsis={
                                          selectedProductsCountNetworkStatus < 7
                                      }
                                  />
                              ),
                          },
                      ]
                    : []),
            ],
            isOMWorkspace
                ? []
                : [
                      {
                          label: {
                              text: contentTeamFromStudioPage
                                  ? ADDED_TO_DRAFT
                                  : ADDED_TO_PROJECT,
                          },
                          component: (
                              <WorkspaceActionBarIconTextWidget
                                  iconColor={
                                      selectedElementFilterType?.value ===
                                      ADDED_TO_PROJECT
                                          ? '#33AEA6'
                                          : '#FFFFFF'
                                  }
                                  disabled={areElementsLoadingOrNotReady}
                                  icon={AddToProjectIcon}
                                  caption={`                            ${
                                      elementsAddedToProjectNetworkStatus < 7
                                          ? ''
                                          : elementsAddedToProject?.getProjectElementNodesTotalCount ??
                                            0
                                  } items`}
                                  hasEllipsis={
                                      elementsAddedToProjectNetworkStatus < 7
                                  }
                                  onIconClick={() =>
                                      handleChangeFilterOption(
                                          selectedElementFilterType?.value ===
                                              'Added to Work Results' ||
                                              selectedElementFilterType?.value ===
                                                  ADDED_TO_PROJECT
                                              ? {
                                                    type:
                                                        selectedElementFilterType?.type,
                                                    value: ALL_PROJECTS_ELEMENTS_TEXT,
                                                }
                                              : {
                                                    type:
                                                        selectedElementFilterType?.type,
                                                    value: ADDED_TO_PROJECT,
                                                }
                                      )
                                  }
                                  name="addedToProject"
                              />
                          ),
                      },
                  ],
            [
                {
                    label: 'Notes',
                    component: (
                        <WorkspaceActionBarButtonWidget
                            onClick={callViewNotes}
                            className="btnViewAllProducts"
                            disabled={projectLoading}
                            label={'Notes'}
                        >
                            View
                        </WorkspaceActionBarButtonWidget>
                    ),
                },
            ],
        ],
        [
            elementsAddedToProjectNetworkStatus,
            projectId,
            handleViewAllProducts,
            projectLoading,
            currentState,
            elementsAddedToProject,
            selectedProductsCount,
            selectedProductsCountNetworkStatus,
            areElementsLoadingOrNotReady,
            elementsListLoading,
            elementsListNetworkStatus,
            onlyElementsAssignedToProjectFilter,
            callViewNotes,
            selectedElementFilterType,
            setStateValue,
        ]
    )

    const rightWidgets = React.useMemo((): WorkspaceActionBarWidget[][] => {
        const widgets = [
            [
                {
                    component: (
                        <WorkspaceActionBarIconButtonWidget
                            icon={TwoColumnIcon}
                            onClick={onToggleShowHideBarDisplay}
                            name="showhide"
                            isActive={isToggleBarVisible}
                        />
                    ),
                    customContainerStyle: classes.customWidgetContainer,
                },
            ],
            [
                {
                    component: (
                        <WorkspaceActionBarIconButtonWidget
                            icon={SearchIcon}
                            onClick={onTriggerSearching}
                            name="search"
                            isActive={isSearchInitiated}
                        />
                    ),
                    customContainerStyle: classes.customWidgetContainer,
                },
            ],
        ]

        if (
            userRoles &&
            !userRoles?.[0]?.includes('Reviewer') &&
            !contentTeamFromStudioPage
        ) {
            return [
                [
                    {
                        component: (
                            <WorkspaceActionBarIconButtonWidget
                                icon={SettingsIcon}
                                onClick={onProjectSettingsClicked}
                                name="settings"
                            />
                        ),
                    },
                ],
            ].concat(widgets)
        }
        return widgets
    }, [
        isSearchInitiated,
        userRoles,
        isToggleBarVisible,
        onProjectSettingsClicked,
        onToggleShowHideBarDisplay,
        onTriggerSearching,
        classes.customWidgetContainer,
        contentTeamFromStudioPage,
    ])
    const renderSelectGroup = (itemType: ElementPanelFilterItem) => {
        const items = itemType.options.map((option, idx) => {
            return (
                <MenuItem
                    onClick={() => handleChangeFilterOption(option)}
                    key={idx}
                    value={option?.value}
                    data-testid={`li-${option.type}-${option.value.replace(
                        / /g,
                        ''
                    )}`}
                >
                    {option?.value}
                </MenuItem>
            )
        })
        return [<ListSubheader>{itemType?.type}</ListSubheader>, items]
    }

    const addBulkElementsToProject = React.useCallback(() => {
        // Collect all baseElementIds from selectedBulkAddElements
        const baseElementIds = (selectedBulkAddElements ?? [])
            .map((element) => element.baseElementId)
            .filter((id): id is string => !!id)

        // Store the baseElementIds in the bulk add store
        setBulkAddBaseElementIds(baseElementIds)

        // Turn on flag which will show the dialog, which will in turn trigger the mutation
        setAddingBulkItemsInProgress(true)
        setAddedBulkAddItemsCount(selectedBulkAddElements?.length ?? 0)
    }, [
        selectedBulkAddElements,
        setAddingBulkItemsInProgress,
        setAddedBulkAddItemsCount,
        setBulkAddBaseElementIds,
    ])

    const clearBulkAddSelection = React.useCallback(
        (isBulkClear: boolean) => () => {
            if (isBulkClear) {
                setSelectedBulkAddElements([])
            }
            setCancelBulkItems(false)
        },
        [setSelectedBulkAddElements, setCancelBulkItems]
    )

    const handleBulkAddCompleted = React.useCallback(
        (response: Partial<ProjectBulkAddResult>) => {
            const addedElements = response?.addedElements
            if (!addedElements) return

            setAddingBulkItemsInProgress(false)
            setAddBulkItemsInProgress(false)
            setAddedBulkAddItemsCount(selectedBulkAddElements?.length ?? 0)
            setBulkAddSkippedElementPaths(response?.skippedElementPaths ?? [])

            // Single pass: collect last ASSEMBLY nodes AND filter in one iteration
            const updatedElements: ProjectElementNode[] = []
            const lastAssemblyNodes: ProjectElementNode[] = []

            addedElements.forEach((element: ProjectElementWithProjectNode) => {
                const nodes = element.projectElementNodes
                if (!nodes?.length) return

                // Find PRODUCTFAMILY nodes whose baseElementId matches bulkAddBaseElementIds
                const productFamilyNodes = nodes.filter(
                    (node) =>
                        node.elementType === 'PRODUCTFAMILY' &&
                        node.baseElementId &&
                        bulkAddBaseElementIds.includes(node.baseElementId)
                )

                if (productFamilyNodes.length > 0) {
                    // For each PRODUCTFAMILY in bulk add, find its parent assembly by parentNodeId
                    productFamilyNodes.forEach((pfNode) => {
                        if (pfNode.parentNodeId) {
                            const assembly = nodes.find(
                                (node) =>
                                    node.id === pfNode.parentNodeId &&
                                    node.elementType === 'ASSEMBLY'
                            )
                            if (
                                assembly &&
                                !lastAssemblyNodes.some(
                                    (a) => a.id === assembly.id
                                )
                            ) {
                                lastAssemblyNodes.push(assembly)
                            }
                        }
                    })
                } else {
                    // Fallback: Find last ASSEMBLY node (reverse search) if no PRODUCTFAMILY nodes in bulk add
                    for (let i = nodes.length - 1; i >= 0; i--) {
                        if (nodes[i].elementType === 'ASSEMBLY') {
                            lastAssemblyNodes.push(nodes[i])
                            break
                        }
                    }
                }

                // Filter and add matching nodes directly
                nodes.forEach((node: any) => {
                    if (
                        node.baseElementId &&
                        bulkAddBaseElementIds.includes(node.baseElementId)
                    ) {
                        updatedElements.push(node)
                    }
                })
            })

            // Combine results
            updatedElements.push(...lastAssemblyNodes)

            if (updatedElements.length > 0) {
                updateElementList!(
                    selectedElementFilterType?.type === 'DIVISIONS'
                        ? updatedDivisionElements(
                              updatedElements,
                              categoryElementStates?.[
                                  selectedCategory?.id ?? 'all'
                              ]?.data
                          )
                        : updatedElements
                )
            }

            setAddToProjectSuccess(true)
            setBulkAddBaseElementIds([])
        },
        [
            bulkAddBaseElementIds,
            selectedBulkAddElements?.length,
            selectedElementFilterType?.type,
            selectedCategory?.id,
            setAddBulkItemsInProgress,
            setBulkAddBaseElementIds,
            updateElementList,
            updatedDivisionElements,
            categoryElementStates,
        ]
    )
    const handleBulkAddError = React.useCallback(
        (errorMessage: string | null | undefined) => {
            showGlobalSnackbar({
                variables: {
                    message:
                        errorMessage ??
                        'An unspecified error was encountered while adding elements.',
                    variant: 'error',
                    id: `WorkspacePage_BulkElementAddError`,
                },
            })
            setAddingBulkItemsInProgress(false)
            setAddBulkItemsInProgress(false)
            clearBulkAddSelection(true)

            // Clear the stored baseElementIds on error as well
            setBulkAddBaseElementIds([])

            // Clear Zustand error to prevent re-triggering on workspace switch
            setBulkAddErrorMessage(undefined)
        },
        [
            addingBulkItemsInProgress,
            showGlobalSnackbar,
            clearBulkAddSelection,
            setAddBulkItemsInProgress,
            setBulkAddBaseElementIds,
            setBulkAddErrorMessage,
        ]
    )

    const closeSuccessDialog = React.useCallback(() => {
        setAddToProjectSuccess(false)
        setAddedBulkAddItemsCount(0)
        setBulkAddSkippedElementPaths([])
        setSelectedBulkAddElements([])
        // Clear Zustand store to prevent re-triggering on workspace switch
        setBulkAddResponse(undefined)
        setBulkAddErrorMessage(undefined)
    }, [
        setAddToProjectSuccess,
        setSelectedBulkAddElements,
        setBulkAddResponse,
        setBulkAddErrorMessage,
    ])
    const noop = React.useCallback(() => {}, [])
    React.useEffect(() => {
        if (previousIsRedirect && !isFromRedirect) {
            setSkipDefaultQuery(true)
        }
    }, [isFromRedirect, previousIsRedirect])

    const resetBulkAddElements = React.useCallback(() => {
        setCancelBulkItems(true)
    }, [setCancelBulkItems])

    React.useEffect(() => {
        if ((elementStates?.data || []).length === 0) {
            updateLocalStorageElementValues('selectedFamily', '')
            updateLocalStorageElementValues('selectedFamilyId', '')
            updateLocalStorageElementValues('selectedProductType', '')
            updateLocalStorageElementValues('selectedNodeId', '')
            updateLocalStorageElementValues('selectedElementType', '')
            updateLocalStorageElementValues('selectedElementId', '')
            updateLocalStorageElementValues('isLastSubAssembly', false)
            updateLocalStorageElementValues('selectedAssemblyPath', '')
            updateLocalStorageElementValues('selectedParentNodeId', '')
            updateLocalStorageElementValues('divisionNumber', '')
            updateLocalStorageElementValues('selectedFilterType', '')
            updateLocalStorageElementValues('categoryNodeId', '')

            setSelectedProjectElementNode(null)
            setSelectedElementIdentifier(undefined)
            // send serviceWorker change
            serviceWorker?.controller?.postMessage({
                [WORKSPACE_PRODUCT_LISTING_KEY]: {
                    category: selectedCategory,
                    family: '',
                    productType: '',
                    projectId,
                    elementId: '',
                    elementType: undefined,
                    id: undefined,
                    elementNode: undefined,
                    specAgentIdPt: undefined,
                    isLastSubAssembly: false,
                    assemblyPath: undefined,
                    parentNodeId: undefined,
                    divisionNumber: undefined,
                    selectedFilterType: undefined,
                    categoryNodeId: undefined,
                } as WorkspaceProductListing,
            })
        } else {
            const changedElement = getNodeByItemIdentifier(
                elementStates?.data,
                selectedElementIdentifier,
                selectedElementFilterType?.type
            )

            if (changedElement && isRemoveNuggetGqlOngoing) {
                changedElement.elementId = null
            }
            const parentFamilyElement =
                changedElement?.elementType === 'PRODUCTTYPE'
                    ? elementStates?.data?.find((element) => {
                          return element.id === changedElement?.parentNodeId
                      })
                    : ({} as ProjectElementNode)
            if (!!changedElement) {
                setSelectedProjectElementNode(changedElement)
                setWorkspaceSelectedElement(changedElement)
                setIsRemoveNuggetGqlOngoing(false)
                if (!changedElement?.isInProject) {
                    setElementNotesCount(0)
                }
            } else {
                setSelectedProjectElementNode(null)
                setWorkspaceSelectedElement(null)
                setElementNotesCount(0)
                setIsRemoveNuggetGqlOngoing(false)
            }

            let family = ''
            let productType = ''
            let parentNodeId = ''

            if (!!changedElement) {
                if (changedElement.elementType === 'PRODUCTFAMILY') {
                    family = changedElement.text.split('|').join(' - ')
                    parentNodeId = changedElement.parentNodeId ?? ''
                } else if (changedElement.elementType === 'PRODUCTTYPE') {
                    const splitParantPath = changedElement.parentTreePath.split(
                        '||'
                    )
                    family = splitParantPath[splitParantPath.length - 1]
                        .split('|')
                        .join(' - ')
                    productType = changedElement.text
                    parentNodeId = changedElement.parentNodeId ?? ''
                }
            }

            // Needs to be a selected product type

            const elementId =
                changedElement?.elementId ?? changedElement?.baseElementId

            const elementType = changedElement?.elementType

            // Needs to be a selected product type
            const nodeId = changedElement?.id

            // Indicator that the node is the last sub assembly
            const isLastSubAssembly = changedElement?.isLastSubAssembly

            const familyId =
                elementType === 'PRODUCTTYPE'
                    ? parentFamilyElement?.elementId
                    : elementType === 'PRODUCTFAMILY'
                    ? elementId
                    : null

            // Assembly Path
            const selectedAssemblyPath = getAssemblyPathByElement(
                changedElement as ProjectElementNode
            )
            const divisionNumber = getDivisionNumberOfSelectedDivision(
                changedElement!,
                selectedElementFilterType?.type
            )
            const selectedFilterType = selectedElementFilterType?.type
            // Set node Id of the category breadcrumb in the dialog
            const categoryNodeId =
                elementType === 'PRODUCTTYPE'
                    ? parentFamilyElement?.parentNodeId
                    : elementType === 'PRODUCTFAMILY'
                    ? changedElement?.parentNodeId
                    : null
            persistState?.('selectedFamily', family)
            persistState?.('selectedFamilyId', familyId)
            persistState?.('selectedProductType', productType)
            persistState?.('selectedParentNodeId', parentNodeId)
            persistState?.('selectedElementId', elementId ?? '')
            persistState?.('selectedElementType', elementType ?? '')
            persistState?.('selectedNodeId', nodeId ?? '')
            persistState?.('isLastSubAssembly', isLastSubAssembly ?? false)
            persistState?.('selectedAssemblyPath', selectedAssemblyPath)
            persistState?.('propertyOption', undefined)
            persistState?.('divisionNumber', divisionNumber)
            persistState?.('selectedFilterType', selectedFilterType)
            persistState?.('categoryNodeId', categoryNodeId)

            // send serviceWorker change
            serviceWorker?.controller?.postMessage({
                [WORKSPACE_PRODUCT_LISTING_KEY]: {
                    category: selectedCategory,
                    family,
                    productType,
                    projectId,
                    elementId,
                    elementType,
                    nodeId,
                    baseElementId: selectedProjectElementNode?.baseElementId,
                    treePath: selectedProjectElementNode?.treePath,
                    isInProject: selectedProjectElementNode?.isInProject,
                    elementNode: selectedProjectElementNode,
                    specAgentIdPt: selectedSpecAgentIdPt,
                    isLastSubAssembly,
                    assemblyPath: selectedAssemblyPath,
                    parentNodeId,
                    updateBODList: true,
                    divisionNumber: divisionNumber,
                    selectedFilterType: selectedFilterType,
                    categoryNodeId: categoryNodeId,
                } as WorkspaceProductListing,
            })
        }
    }, [
        selectedElementIdentifier,
        selectedElementFilterType,
        selectedSpecAgentIdPt,
        elementStates,
        persistState,
        projectId,
        selectedCategory,
        serviceWorker,
        selectedProjectElementNode,
        searchFilters,
        getDivisionNumberOfSelectedDivision,
    ])

    // Reset state from redirect
    React.useEffect(() => {
        if (
            !targetElementFound &&
            selectedElementIdFromDesignProgressTable &&
            !currentState['elementsInProjectFilterWidgetActive']
        ) {
            // Force Added To Project filter on.
            setStateValue('elementsInProjectFilterWidgetActive', true, true)
            setStateValue(
                'elementPanelFilterType',
                selectedElementFilterType,
                true
            )

            // Force select all categories
            setStateValue(
                'elementPanelFilterType',
                { value: ADDED_TO_PROJECT, type: 'ASSEMBLIES' },
                true
            )
            persistState?.('selectedCategory', {
                id: 'all',
                name: 'All Categories',
            })
        }
    }, [
        selectedElementIdFromDesignProgressTable,
        persistState,
        currentState,
        setStateValue,
        targetElementFound,
        selectedElementFilterType,
        history,
        isDivisionViewNodeValue,
    ])
    React.useEffect(() => {
        setWorkspaceContentItems()
    }, [])

    React.useEffect(() => {
        if (fromStudio) {
            setFromStudiovalue(fromStudio)
            setisDivisionViewNodeValue(isDivisionViewNode)
        }
    }, [fromStudio, isDivisionViewNode])
    React.useEffect(() => {
        if (
            (isFromRedirect || fromStudioValue || selectedNodeFromBP) &&
            !targetElementFound &&
            !showElementsPanelFullLoadState &&
            categoryElementStates[selectedCategory?.id ?? 'all'] &&
            selectedCategory?.id === 'all' &&
            selectedElementFilterType !== undefined
        ) {
            if (
                previousElementsAssignedToProjectFilter ===
                    onlyElementsAssignedToProjectFilter &&
                JSON.stringify(previewElementFilterType) ===
                    JSON.stringify(selectedElementFilterType)
            ) {
                const elementList =
                    categoryElementStates[selectedCategory.id].data
                const compareId =
                    fromStudioValue || selectedNodeFromBP
                        ? projectElementNodeId ?? selectedNodeFromBP
                        : selectedElementIdFromDesignProgressTableInitialValue

                const targetElement = elementList?.find(
                    (elementItem: ProjectElementNode) =>
                        fromStudioValue || selectedNodeFromBP
                            ? elementItem.id === compareId
                            : elementItem.elementId === compareId
                )

                if (targetElement) {
                    setSkipDefaultQuery(true)
                    setTargetElementFound(true)
                    setSelectedElementIdentifier({
                        baseElementId: targetElement.baseElementId,
                        treePath: targetElement.treePath,
                        id: targetElement.id!,
                    })
                    history.replace(location.pathname, {})
                    const filterDataForRedirect = {
                        value: contentTeamFromStudioPage
                            ? ALL_ELEMENTS_TEXT
                            : ADDED_TO_PROJECT,
                        type: fromStudioValue
                            ? isDivisionViewNodeValue
                                ? 'DIVISIONS'
                                : 'ASSEMBLIES'
                            : getPersistedState?.('elementPanelFilterType')
                                  ?.type ?? 'ASSEMBLIES',
                    }
                    setSelectedElementIdFromDesignProgressTable('')
                    setSelectedElementFilterType(filterDataForRedirect)
                    setStateValue(
                        'elementPanelFilterType',
                        filterDataForRedirect,
                        true
                    )
                } else if (isFromRedirect) {
                    /**
                     *  change the filter type to assemblies if the selected filter type is divisions
                     *  when redirecting from project overview page
                     */
                    if (selectedElementFilterType?.type === 'DIVISIONS') {
                        setSelectedElementFilterType({
                            value: ADDED_TO_PROJECT,
                            type: 'ASSEMBLIES',
                        })
                    }

                    setSelectedProjectElementNode(null)
                    setWorkspaceSelectedElement(null)
                    setElementNotesCount(0)
                    getMoreElementsList(
                        true,
                        undefined,
                        selectedElementIdFromDesignProgressTableInitialValue
                    )
                    setSkipDefaultQuery(true)
                }
            } else {
                getMoreElementsList(
                    true,
                    undefined,
                    selectedElementIdFromDesignProgressTableInitialValue
                )
                setSkipDefaultQuery(true)
            }
        }
    }, [
        history,
        location,
        isFromRedirect,
        targetElementFound,
        getPersistedState,
        elementStates,
        getMoreElementsList,
        selectedElementIdFromDesignProgressTableInitialValue,
        setSelectedElementIdentifier,
        selectedCategory,
        onlyElementsAssignedToProjectFilter,
        previousElementsAssignedToProjectFilter,
        previewElementFilterType,
        showElementsPanelFullLoadState,
        categoryElementStates,
        fromStudioValue,
        isDivisionViewNodeValue,
        projectElementNodeId,
        currentUserActiveFirm,
        selectedNodeFromBP,
        selectedElementFilterType,
        currentState,
        skipDefaultQuery,
        selectedProjectElementNode,
        selectedElementFilterType,
    ])

    React.useEffect(() => {
        updateBrowserTitle?.(
            projectData?.getProject?.name
                ? `${projectData?.getProject?.name} - Workspace`
                : 'Workspace'
        )
    }, [projectData, updateBrowserTitle])

    React.useEffect(() => {
        if (!isBetaWorkspaceToggleEnabled) {
            onStateChange &&
                setCustomActionBar(
                    <WorkspaceActionBar
                        onStateChange={onStateChange}
                        currentState={currentState}
                        left={leftWidgets}
                        right={rightWidgets}
                    />
                )
        } else {
            setActionBarVisibility(false)
        }
    }, [
        leftWidgets,
        setCustomActionBar,
        rightWidgets,
        onStateChange,
        currentState,
        isBetaWorkspaceToggleEnabled,
        setActionBarVisibility,
    ])
    const [selectedNuggetData, setSelectedNuggetData] = React.useState<
        GetProjectNuggetResponse
    >()

    const [loadingBackdrop, setLoadingBackdrop] = React.useState<boolean>(false)

    const [isElementNotesVisible, setIsElementNoteVisible] = React.useState<
        boolean
    >(true)
    const [
        isElementPropertiesVisible,
        setIsElementPropertiesVisible,
    ] = React.useState<boolean>(true)
    const showLeftMostPane = useElementTreeStore(
        (store) => store.showLeftMostPane
    )
    const setShowLeftMostPane = useElementTreeStore(
        (store) => store.setshowLeftMostPane
    )

    const currentPane = useElementTreeStore((store) => store.currentPane)
    const handleCollapseLeftPanel = React.useCallback(() => {
        setShowLeftMostPane?.(!showLeftMostPane)
    }, [showLeftMostPane])
    const collapseIcon = React.useMemo(() => {
        return (
            <HoverTooltip
                className={classes.collapseIcon}
                text="Collapse Panel"
                placements={['left']}
            >
                <div
                    data-testid="collapseIconLeftPane"
                    onClick={handleCollapseLeftPanel}
                >
                    {' '}
                    <CollapseIcon />{' '}
                </div>
            </HoverTooltip>
        )
    }, [handleCollapseLeftPanel])
    const elementToggleHandler = React.useCallback(
        (
            toggleType: 'notes' | 'properties' | 'category' | 'elements',
            value: boolean | undefined
        ) => {
            switch (toggleType) {
                case 'notes':
                    setIsElementNoteVisible(value ?? true)
                    break
                case 'properties':
                    setIsElementPropertiesVisible(value ?? true)
                    break
                case 'category':
                    setIsCategoryPaneVisible(value ?? true)
                    break
                case 'elements':
                    setIsElementPaneVisible(value ?? true)
                    break
            }
        },
        []
    )
    const handleChangeSelectGroupping = React.useCallback(
        (value: ElementPanelSubItem) => {
            setSelectedElementFilterType(value)
            setStateValue('elementPanelFilterType', value, true)
            if (
                isBetaWorkspaceToggleEnabled &&
                selectedElementFilterType?.type === 'ASSEMBLIES' &&
                value?.type === 'DIVISIONS'
            ) {
                handleResetCategories()
            }
        },
        [setStateValue, selectedElementFilterType, isBetaWorkspaceToggleEnabled]
    )

    const {
        handleRetrieveUpdatedDtvPerPartType,
        handleRetrieveUpdatedDTV,
    } = React.useContext(WorkspaceContentContext)

    // Read data from service worker
    React.useEffect(() => {
        if (!serviceWorker) return

        const handleServiceWorkerMessage = ({ data }: MessageEvent) => {
            // make sure that the data coming from SW is for this page.
            const workspaceProductListingState =
                data[WORKSPACE_PRODUCT_LISTING_KEY]
            if (workspaceProductListingState) {
                // check if same project id is being serviced
                if (workspaceProductListingState.projectId !== projectId) return

                setSelectedCategory(workspaceProductListingState.category)
            }
            const workspaceProductListingActionState =
                data[WORKSPACE_PRODUCT_LISTING_ACTION]

            if (workspaceProductListingActionState) {
                setProjectProductListingCount(
                    workspaceProductListingActionState?.selectedProducts
                )
                refetchSelectedProductsCount()
            }

            // Handle category change from ProductListingDialog
            const workspaceCategoryChangeState =
                data[WORKSPACE_CATEGORY_CHANGE_KEY]
            if (workspaceCategoryChangeState) {
                handleSelectedCatgoryChange(
                    workspaceCategoryChangeState.category,
                    workspaceCategoryChangeState.discipline
                )
            }
        }
        const handleServiceWorkerElementMessage = ({ data }: MessageEvent) => {
            const workspaceTreeProjectElementsState = data[
                WORKSPACE_TREE_ELEMENT_NODES
            ] as WorkspaceTreeElementNodes

            if (workspaceTreeProjectElementsState) {
                if (
                    workspaceTreeProjectElementsState?.updatedElementId &&
                    !selectedProjectElementNode?.hasChildren &&
                    (onlyElementsAssignedToProjectFilter ||
                        selectedElementFilterType?.value ===
                            'Added to Work Results')
                ) {
                    const selectedPTElement = workspaceTreeProjectElementsState?.projectElementNodes?.filter(
                        (item) =>
                            item?.elementId ===
                            workspaceTreeProjectElementsState?.updatedElementId
                    )?.[0]
                    let currentElementsList = categoryElementStates[
                        selectedCategory?.id ?? 'all'
                    ]?.data?.map((item) => {
                        const checkItemNodes =
                            item?.treePath ===
                                selectedPTElement?.parentTreePath ||
                            item?.elementId ===
                                selectedPTElement?.parentNodeId ||
                            item?.id === selectedPTElement?.parentNodeId
                        if (
                            checkItemNodes ||
                            (onlyElementsAssignedToProjectFilter &&
                                item?.isInProject)
                        ) {
                            return {
                                ...item,
                                hasChildren: true,
                                hasSubItems: true,
                            }
                        } else return item
                    })
                    if (
                        currentElementsList?.filter(
                            (item) =>
                                item?.elementId ===
                                workspaceTreeProjectElementsState?.updatedElementId
                        )?.length === 0
                    ) {
                        const indexToInsert = currentElementsList?.findIndex(
                            (item) =>
                                item?.treePath ===
                                    selectedPTElement?.parentTreePath ||
                                item.elementId ===
                                    selectedPTElement.parentNodeId ||
                                item?.id === selectedPTElement?.parentNodeId
                        )

                        currentElementsList?.splice(
                            indexToInsert + 1,
                            0,
                            selectedPTElement
                        )
                        const newCategoryElementState: Record<
                            string,
                            CategoryElementListStateInfo
                        > = JSON.parse(JSON.stringify(categoryElementStates))
                        newCategoryElementState[
                            selectedCategory?.id ?? 'all'
                        ] = {
                            ...newCategoryElementState[
                                selectedCategory?.id ?? 'all'
                            ],
                            data: currentElementsList,
                            totalElementCount: currentElementsList.length,
                        }
                        setCategoryElementStates(newCategoryElementState)
                    }
                } else {
                    // For nodes with children (assembly/division view), try targeted insertion
                    // to preserve the expanded tree state. Calling getMoreElementsList with
                    // forceReset=true wipes the flat list, causing visible nodes to go missing.
                    // Instead, find the newly added PT in projectElementNodes and insert it
                    // additively. If no match is found, skip the update entirely — the tree
                    // stays as-is and the user can expand manually to see the new PT.
                    const updatedElementId =
                        workspaceTreeProjectElementsState?.updatedElementId
                    const ptNode = updatedElementId
                        ? workspaceTreeProjectElementsState?.projectElementNodes?.find(
                              (n) => n?.elementId === updatedElementId
                          )
                        : undefined
                    if (ptNode) {
                        insertNewProductTypeElement(
                            ptNode,
                            false,
                            undefined,
                            undefined,
                            workspaceTreeProjectElementsState?.projectElementNodes
                        )
                    }
                }
                setTimeout(() => {
                    refetchAddedElementsCount && refetchAddedElementsCount()
                    refetchSelectedProductsCount()
                }, 1000)
            }
        }

        serviceWorker?.addEventListener('message', handleServiceWorkerMessage)
        serviceWorker?.addEventListener(
            'message',
            handleServiceWorkerElementMessage
        )

        return () => {
            serviceWorker?.removeEventListener(
                'message',
                handleServiceWorkerMessage
            )
            serviceWorker?.removeEventListener(
                'message',
                handleServiceWorkerElementMessage
            )
        }
    }, [
        serviceWorker,
        projectId,
        selectedProjectElementNode,
        updateElementList,
        insertNewProductTypeElement,
        refetchSelectedProductsCount,
        setProjectProductListingCount,
        handleSelectedCatgoryChange,
    ])

    // Do initial fetch of elements list data
    React.useEffect(() => {
        if (
            Object.keys(categoryElementStates ?? {}).length > 0 &&
            isFetchingMoreElements &&
            selectedElementFilterType
        ) {
            if ((elementStates?.elementSkipCount ?? 0) === 0) {
                getMoreElementsList(true, 'root')
            }
        }
    }, [
        getMoreElementsList,
        isFetchingMoreElements,
        elementStates,
        categoryElementStates,
        selectedElementFilterType,
    ])
    React.useEffect(() => {
        if (fromStudioValue && fetchAllElements) {
            getMoreElementsList(true, 'root')
        }
    }, [fromStudioValue, fetchAllElements])

    React.useEffect(() => {
        //update elements list after best practices progress completed while creating project
        if (refreshProjectElementNodes) {
            getMoreElementsList(true, 'root')
        }
    }, [refreshProjectElementNodes])

    //Reset categoryElementStates when onlyAssignedToProjects flag changes value
    React.useEffect(() => {
        if (
            (!!previousElementsAssignedToProjectFilter !==
                !!onlyElementsAssignedToProjectFilter ||
                JSON.stringify(previewElementFilterType) !==
                    JSON.stringify(selectedElementFilterType)) &&
            Object.keys(categoryElementStates).length > 0
        ) {
            if (
                !isFromRedirect &&
                !previousIsRedirect &&
                !fromStudioValue &&
                !selectedNodeFromBP
            ) {
                setShowElementsPanelFullLoadState(true)
                getMoreElementsList(true, 'root')
            }
        }
    }, [
        onlyElementsAssignedToProjectFilter,
        previousElementsAssignedToProjectFilter,
        previewElementFilterType,
        categoryElementStates,
        setCategoryElementStates,
        getMoreElementsList,
        isFromRedirect,
        previousIsRedirect,
        searchFilters,
        fromStudioValue,
        getPersistedState,
        selectedNodeFromBP,
    ])

    React.useEffect(() => {
        if (
            uniformatClassificationError ||
            projectUniformatClassificationError ||
            projectUniformatClassificationElementError
        ) {
            const messageError =
                uniformatClassificationError?.message ||
                projectUniformatClassificationError?.message ||
                projectUniformatClassificationElementError?.message

            showGlobalSnackbar({
                variables: {
                    message: messageError ?? '',
                    variant: 'error',
                    id:
                        'ProjectWorkspaceContentArea_ProjectUniformatClassification',
                },
            })
        }
    }, [
        showGlobalSnackbar,
        uniformatClassificationError,
        projectUniformatClassificationError,
        projectUniformatClassificationElementError,
    ])

    React.useMemo(() => {
        if (
            !selectedTopic &&
            selectedProjectElementNode?.elementType === 'ASSEMBLY'
        ) {
            if (selectedProjectElementNode?.elementId) {
                getProjectUniformatClassification({
                    variables: {
                        input: {
                            elementId: selectedProjectElementNode?.elementId,
                            projectId,
                        },
                    },
                })
                getProjectUniformatClassificationElement({
                    variables: {
                        input: {
                            elementId: selectedProjectElementNode?.elementId,
                            projectId,
                        },
                    },
                })
            } else {
                if (selectedProjectElementNode?.baseElementId) {
                    getUniformatClassification({
                        variables: {
                            input: {
                                uniformatClassificationId:
                                    selectedProjectElementNode?.baseElementId,
                            },
                        },
                    })
                } else {
                }
            }
        }
    }, [
        getProjectUniformatClassificationElement,
        getProjectUniformatClassification,
        projectId,
        getUniformatClassification,
        selectedProjectElementNode,
    ])
    const categoryPanel = React.useMemo(() => {
        return (
            <div
                className={classes.fullWidthPaneCategoryContainer}
                id="categoryPanelLeftMostPane"
            >
                <CategoryPane
                    disciplineData={allDisciplineData}
                    categoriesData={
                        selectedDiscipline?.categories ??
                        allDisciplineData[0]?.categories
                    }
                    selectedCategory={selectedCategory}
                    selectedDiscipline={
                        selectedDiscipline ?? allDisciplineData[0]
                    }
                    handleCategorySelection={(
                        dataCategory: CategoryPanel,
                        dataDiscipline: ProfessionalDisciplineInner
                    ) => {
                        handleSelectedCatgoryChange(
                            dataCategory,
                            dataDiscipline
                        )
                    }}
                    handleCollapseLeftPanel={handleCollapseLeftPanel}
                    collapseIcon={collapseIcon}
                />
            </div>
        )
    }, [allDisciplineData, selectedDiscipline, selectedCategory])
    const elementsPanel = React.useMemo(() => {
        return selectedTopic === undefined ? (
            <ElementsPanel
                data-testid="elementPanel"
                panelTitle={
                    searchFilters
                        ? elementsListLoading ||
                          getProjectElementNodesLoading ||
                          showElementsPanelFullLoadState
                            ? 'Search Results'
                            : `Search Results (${
                                  elementStates?.totalElementCount ?? 0
                              })`
                        : isWhitespaceSearch
                        ? 'Search Results (0)'
                        : `${selectedElementFilterType?.type}: ${selectedElementFilterType?.value}`
                }
                selectedElementFormat={selectedElementFilterType?.type}
                selectedElementIdentifier={selectedElementIdentifier}
                isNewDataLoading={isNewDataLoading}
                onLoadMoreClick={getMoreElementsList}
                parentNodeIdPendingBulkSelect={parentNodeIdPendingBulkSelect}
                handleBPElementTreeChange={handleBPElementTreeChange}
                hasMoreElementsToFetch={elementStates?.hasMoreElementsToFetch}
                onSelectedItemIdentifierChange={handleElementItemCardClick}
                savePreviousElementForPersistance={
                    handleSavePreviousElementForPersistance
                }
                elementFlatList={
                    selectedElementFilterType?.value ===
                        'Added to Work Results' &&
                    selectedCategory?.name !== 'Work Results'
                        ? []
                        : categoryElementStates?.[selectedCategory?.id ?? 'all']
                              ?.data
                }
                showFullLoadState={showElementsPanelFullLoadState}
                isLoading={
                    areElementsLoadingOrNotReady ||
                    !categoryElementStates?.[selectedCategory?.id ?? 'all']
                        ?.data ||
                    showElementsPanelFullLoadState
                }
                totalElementCount={elementStates?.totalElementCount}
                famNumberFormat={projectSettings?.familyNumberFormat}
                searchFilters={searchFilters}
                isWhitespaceSearch={isWhitespaceSearch}
                onLoadChildren={(
                    id: string,
                    isBulkSelectRequested: boolean = false
                ) =>
                    getMoreElementsList(
                        false,
                        id,
                        undefined,
                        isBulkSelectRequested
                    )
                }
                redirectKey={
                    selectedElementIdFromDesignProgressTableInitialValue
                }
                contentTeamFromStudioPage={contentTeamFromStudioPage}
                fromStudio={fromStudioValue || selectedNodeFromBP}
                selectedBulkAddElements={selectedBulkAddElements}
                setSelectedBulkAddElements={setSelectedBulkAddElements}
                projectId={projectId!}
                isFromWorkSpacePage={!contentTeamFromStudioPage}
                persistedElement={getPersistedNode()}
                familyNumber={familyNumber}
                familyName={familyName}
                isOMWorkspace={isOMWorkspace}
                updateElementList={updateElementList}
                selectedItemIdFromStudioTable={selectedItemIdFromStudioTable}
            />
        ) : (
            <SustainabilityFamilyTreeList
                sustanaibilityTopicFamilies={sustanaibilityTopicFamilies}
            />
        )
    }, [
        sustanaibilityTopicFamilies,
        selectedItemIdFromStudioTable,
        isOMWorkspace,
        familyName,
        familyNumber,
        contentTeamFromStudioPage,
        projectId,
        selectedBulkAddElements,
        fromStudioValue,
        selectedNodeFromBP,
        selectedElementIdFromDesignProgressTableInitialValue,
        searchFilters,
        isWhitespaceSearch,
        projectSettings,
        elementStates,
        showElementsPanelFullLoadState,
        areElementsLoadingOrNotReady,
        categoryElementStates,
        selectedCategory,
        selectedElementFilterType,
        parentNodeIdPendingBulkSelect,
        isNewDataLoading,
        selectedElementIdentifier,
        elementsListLoading,
        getProjectElementNodesLoading,
        selectedTopic,
    ])
    const searchBarPanel = React.useMemo(() => {
        return (
            isBetaWorkspaceToggleEnabled && (
                <SearchBarLeftMostPane
                    selectedCatergory={selectedCategory}
                    handleSearchElementNodes={executeSearch}
                    resetSearchFilters={resetSearchFilters}
                    onWhitespaceSearch={handleWhitespaceSearch}
                    searchKeyword={searchFilters?.[0]?.searchText}
                    selectedElementFormat={selectedElementFilterType?.type}
                    searchFilters={searchFilters}
                />
            )
        )
    }, [
        selectedCategory,
        searchFilters,
        selectedElementFilterType,
        isBetaWorkspaceToggleEnabled,
    ])
    React.useEffect(() => {
        if (
            !selectedTopic &&
            selectedProjectElementNode?.elementType === 'ASSEMBLY'
        ) {
            setProjectElementMetadata(
                projectUniformatClassificationElementData
                    ?.getProjectUniformatClassificationElement
                    ?.projectElementMetadata as Partial<ProjectElementMetadata>
            )
        }
    }, [projectUniformatClassificationElementData, selectedProjectElementNode])

    React.useEffect(() => {
        if (projectUniformatClassificationData) {
            setSelectedElementData(
                projectUniformatClassificationData.getProjectUniformatClassification
            )
            setElementNotesCount(
                projectUniformatClassificationData
                    ?.getProjectUniformatClassification?.notesCount ?? 0
            )
        }
    }, [projectUniformatClassificationData, setSelectedElementData])

    React.useEffect(() => {
        if (uniformatClassificationData) {
            setSelectedElementData(
                uniformatClassificationData.getUniformatClassification
            )
        }
    }, [uniformatClassificationData, setSelectedElementData])

    const prevIsWhitespaceSearch = usePrevious(isWhitespaceSearch)
    React.useEffect(() => {
        // Refetch the unfiltered tree when leaving a whitespace search.
        // The main searchFilters effect below won't fire because searchFilters
        // didn't change (was undefined before and after a whitespace search).
        if (
            prevIsWhitespaceSearch &&
            !isWhitespaceSearch &&
            !searchFilters &&
            !(elementsListLoading && getProjectElementNodesLoading)
        ) {
            getMoreElementsList(true, 'root')
        }
    }, [
        isWhitespaceSearch,
        prevIsWhitespaceSearch,
        searchFilters,
        getMoreElementsList,
        elementsListLoading,
        getProjectElementNodesLoading,
    ])

    React.useEffect(() => {
        if (
            ((prevSearchFilters && !searchFilters) ||
                prevSearchFilters !== searchFilters) &&
            !(elementsListLoading && getProjectElementNodesLoading)
        ) {
            getMoreElementsList(true, 'root')
        }

        if (searchFilters !== getPersistedState?.('searchFilters')) {
            persistState?.('searchFilters', searchFilters)
        }
    }, [
        searchFilters,
        prevSearchFilters,
        getMoreElementsList,
        getProjectElementNodesLoading,
        elementsListLoading,
        getPersistedState,
        persistState,
    ])
    React.useEffect(() => {
        setProjectSettings(
            projectData?.getProject?.projectSettings as ProjectSettings
        )
    }, [projectData])

    React.useEffect(() => {
        !contentTeamFromStudioPage &&
            addedElememtsToProject({
                variables: {
                    input: {
                        projectId: projectId || '',
                    },
                },
            })
    }, [contentTeamFromStudioPage])

    React.useEffect(() => {
        if (!isBetaWorkspaceToggleEnabled) {
            setIsBulkAddVisible(
                !!selectedBulkAddElements && !!selectedBulkAddElements.length
            )
        }
    }, [
        selectedBulkAddElements,
        setIsBulkAddVisible,
        isBetaWorkspaceToggleEnabled,
    ])
    React.useEffect(() => {
        if (!isBetaWorkspaceToggleEnabled) return
        if (bulkAddResponse) {
            handleBulkAddCompleted(bulkAddResponse)
        }
        if (bulkAddErrorMessage) {
            handleBulkAddError(bulkAddErrorMessage)
        }
    }, [bulkAddResponse, bulkAddErrorMessage, isBetaWorkspaceToggleEnabled])
    return (
        <>
            {isOMWorkspace && omQueryTimeout && elementsListLoading && (
                <Box className={classes.omTimeoutAlert}>
                    <Alert
                        severity="warning"
                        onClose={() => {
                            setOmQueryTimeout(false)
                            // Clean up timers when user dismisses alert
                            if (omTimeoutTimersRef.current.interval) {
                                clearInterval(
                                    omTimeoutTimersRef.current.interval
                                )
                                omTimeoutTimersRef.current.interval = null
                            }
                            if (omTimeoutTimersRef.current.timeout) {
                                clearTimeout(omTimeoutTimersRef.current.timeout)
                                omTimeoutTimersRef.current.timeout = null
                            }
                        }}
                    >
                        <AlertTitle>
                            Large Family Loading - This may take a while
                        </AlertTitle>
                        This family contains a large amount of data and is
                        taking longer than expected to load (
                        {omQueryTimeoutSeconds}s elapsed).
                        <br />
                        <strong>
                            Please wait while the content loads, or consider
                            using the new Beta Workspace for better performance
                            with large families.
                        </strong>
                        <br />
                        <br />
                        <em>
                            Note: This is a known limitation with families
                            containing thousands of elements. The development
                            team is working on pagination support to improve
                            this experience.
                        </em>
                    </Alert>
                </Box>
            )}

            {!!allDisciplineData && allDisciplineData.length !== 0 ? (
                <GridContainer direction="column">
                    {isToggleBarVisible &&
                        (isBetaWorkspaceToggleEnabled ? (
                            <Workspace2ActionBar
                                selectedProjectNode={selectedProjectElementNode}
                                selectedTopic={selectedTopic}
                                userId={userId}
                                projectId={projectId}
                                projectName={
                                    projectData?.getProject?.name ?? ''
                                }
                                onProjectSettingsClicked={
                                    onProjectSettingsClicked
                                }
                                isLoading={
                                    selectedElementFilterType?.type ===
                                    'DIVISIONS'
                                        ? (elementsListLoading ||
                                              getProjectElementNodesLoading) ??
                                          false
                                        : selectedProjectElementNode?.elementType ===
                                          'ASSEMBLY'
                                        ? projectUniformatClassificationElementData ===
                                              undefined &&
                                          uniformatClassificationData ===
                                              undefined
                                        : false
                                }
                                contentTeamFromStudioPage={
                                    contentTeamFromStudioPage
                                }
                                addRemoveProjectProps={{
                                    isOnlyAddedToProject:
                                        onlyElementsAssignedToProjectFilter ||
                                        selectedElementFilterType?.value ===
                                            'Added to Work Results',

                                    updateElementList: updateElementList,
                                    selectedElementFormat:
                                        selectedElementFilterType?.type,
                                    allElementsList:
                                        categoryElementStates?.[
                                            selectedCategory?.id ?? 'all'
                                        ]?.data,
                                }}
                            />
                        ) : (
                            <GridItem data-testid="showHideBarItem">
                                <GridContainer
                                    item
                                    className={classes.showHideContainer}
                                    data-testid="showHideBarContainer"
                                >
                                    <ShowHideBar
                                        selectedProjectNode={
                                            selectedProjectElementNode
                                        }
                                        selectedTopic={selectedTopic}
                                        items={showHideBarItems}
                                        userId={userId}
                                        projectId={projectId}
                                    />
                                </GridContainer>
                            </GridItem>
                        ))}
                    {!isBetaWorkspaceToggleEnabled && isSearchInitiated && (
                        <GridItem data-testid="searchBarItem">
                            <GridContainer
                                className={classes.searchBarContainer}
                                data-testid="searchBarContainer"
                            >
                                <SearchBar
                                    selectedCatergory={selectedCategory}
                                    handleSearchElementNodes={executeSearch}
                                    resetSearchFilters={resetSearchFilters}
                                    onWhitespaceSearch={
                                        handleWhitespaceSearch
                                    }
                                    searchKeyword={
                                        searchFilters?.[0]?.searchText
                                    }
                                    selectedElementFormat={
                                        selectedElementFilterType?.type
                                    }
                                    searchFilters={searchFilters}
                                />
                            </GridContainer>
                        </GridItem>
                    )}
                    {isBulkAddVisible && !isBetaWorkspaceToggleEnabled && (
                        <GridItem data-testid="bulkAddItem">
                            <GridContainer
                                className={classes.bulkAddContainer}
                                data-testid="bulkAddContainer"
                            >
                                <BulkAddBar
                                    selectedBulkAddElements={
                                        selectedBulkAddElements
                                    }
                                    resetBulkAddElements={resetBulkAddElements}
                                    addBulkAddItemsToProject={
                                        addBulkElementsToProject
                                    }
                                />
                            </GridContainer>
                        </GridItem>
                    )}
                    <GridItem>
                        <GridContainer
                            className={classes.container}
                            data-testid="workspaceContentArea"
                        >
                            {isBetaWorkspaceToggleEnabled && (
                                <LeftMostPane
                                    categoryNode={categoryPanel}
                                    elementsNode={elementsPanel}
                                    handleCollapseLeftPanel={
                                        handleCollapseLeftPanel
                                    }
                                    selectedElementFilterType={
                                        selectedElementFilterType
                                    }
                                    handleChangeSelectGroupping={
                                        handleChangeSelectGroupping
                                    }
                                    handleChangeFilterOption={
                                        handleChangeFilterOption
                                    }
                                    selectedCategory={selectedCategory}
                                    handleResetCategories={
                                        handleResetCategories
                                    }
                                    searchBar={searchBarPanel}
                                    isSearchInitiated={isSearchInitiated}
                                />
                            )}
                            {!isBetaWorkspaceToggleEnabled &&
                                !contentTeamFromStudioPage && (
                                    <GridItem
                                        className={clsx(
                                            classes.categoriesPane,
                                            classes.panel,
                                            showHideStates['categories']
                                                ? classes.expandedCategoriesPane
                                                : classes.collapsedPane
                                        )}
                                    >
                                        <div
                                            className={
                                                classes.fullWidthPaneCategoryContainer
                                            }
                                            id="categoryPanel"
                                        >
                                            <CategoriesPanel
                                                disciplineData={
                                                    allDisciplineData
                                                }
                                                categoriesData={
                                                    selectedDiscipline?.categories ??
                                                    allDisciplineData[0]
                                                        .categories
                                                }
                                                selectedCategory={
                                                    selectedCategory
                                                }
                                                selectedDiscipline={
                                                    selectedDiscipline ??
                                                    allDisciplineData[0]
                                                }
                                                handleCategorySelection={(
                                                    dataCategory,
                                                    dataDiscipline
                                                ) => {
                                                    handleSelectedCatgoryChange(
                                                        dataCategory,
                                                        dataDiscipline
                                                    )
                                                }}
                                            />
                                        </div>
                                    </GridItem>
                                )}
                            {!isFromRedirect ? (
                                <>
                                    {!isBetaWorkspaceToggleEnabled && (
                                        <GridItem
                                            className={clsx(
                                                classes.elementsPane,
                                                classes.panel,
                                                showHideStates['elements']
                                                    ? classes.expandedPane
                                                    : classes.collapsedPane
                                            )}
                                        >
                                            <div
                                                className={
                                                    classes.fullWidthPaneContentContainer
                                                }
                                            >
                                                {showHideStates['elements'] &&
                                                    !contentTeamFromStudioPage && (
                                                        <GridItem>
                                                            <Select
                                                                data-testid="selectElement"
                                                                MenuProps={{
                                                                    anchorOrigin: {
                                                                        vertical:
                                                                            'bottom',
                                                                        horizontal:
                                                                            'left',
                                                                    },
                                                                    getContentAnchorEl: null,
                                                                }}
                                                                value={
                                                                    selectedElementFilterType
                                                                }
                                                                renderValue={() => (
                                                                    <GridItem
                                                                        className={
                                                                            classes.panelTitleWithGroupping
                                                                        }
                                                                    >
                                                                        <span data-testid="elementsPanelTitle">
                                                                            {
                                                                                selectedElementFilterType?.type
                                                                            }
                                                                        </span>
                                                                        {' : '}
                                                                        <span
                                                                            className={
                                                                                classes.panelTitleContainerGroupValue
                                                                            }
                                                                        >
                                                                            {
                                                                                selectedElementFilterType?.value
                                                                            }
                                                                        </span>
                                                                    </GridItem>
                                                                )}
                                                                onChange={(e) =>
                                                                    handleChangeFilterOption(
                                                                        undefined,
                                                                        e
                                                                    )
                                                                }
                                                                className={
                                                                    classes.inputSelectText
                                                                }
                                                                disableUnderline
                                                            >
                                                                {updatedFilteroptions?.map(
                                                                    (
                                                                        itemType
                                                                    ) =>
                                                                        renderSelectGroup(
                                                                            itemType
                                                                        )
                                                                )}
                                                            </Select>
                                                        </GridItem>
                                                    )}

                                                {elementsPanel}
                                            </div>
                                        </GridItem>
                                    )}
                                    <GridItem
                                        xs={true}
                                        style={{
                                            backgroundColor: isBetaWorkspaceToggleEnabled
                                                ? '#EDF0F6'
                                                : '#FFFFFF',
                                        }}
                                        className={clsx(
                                            classes.mainContentPane,
                                            !isBetaWorkspaceToggleEnabled &&
                                                classes.padded
                                        )}
                                    >
                                        <RightMostPaneProvider>
                                            <WorkspaceContentArea
                                                data-testid="workspaceContentArea"
                                                projectId={projectId!}
                                                updateElementList={
                                                    updateElementList
                                                }
                                                allElementsList={
                                                    categoryElementStates?.[
                                                        selectedCategory?.id ??
                                                            'all'
                                                    ]?.data
                                                }
                                                selectedTopic={selectedTopic}
                                                handleUpdateSelectedTopic={
                                                    handleUpdateSelectedTopic
                                                }
                                                persistState={persistState}
                                                getPersistedState={
                                                    getPersistedState
                                                }
                                                handleUpdatePUCPropertyValue={
                                                    handleUpdatePUCPropertyValue
                                                }
                                                selectedElementFormat={
                                                    selectedElementFilterType?.type
                                                }
                                                isDataLoading={
                                                    selectedElementFilterType?.type ===
                                                    'DIVISIONS'
                                                        ? (elementsListLoading ||
                                                              getProjectElementNodesLoading) ??
                                                          false
                                                        : selectedProjectElementNode?.elementType ===
                                                          'ASSEMBLY'
                                                        ? projectUniformatClassificationElementData ===
                                                              undefined &&
                                                          uniformatClassificationData ===
                                                              undefined
                                                        : false
                                                }
                                                isBulkAddDataLoading={
                                                    isBulkAddDataLoading
                                                }
                                                selectedProjectElementNode={
                                                    selectedProjectElementNode
                                                }
                                                selectedElementData={
                                                    selectedElementFilterType?.type !==
                                                        'DIVISIONS' &&
                                                    selectedProjectElementNode?.elementType ===
                                                        'ASSEMBLY'
                                                        ? selectedElementData
                                                        : undefined
                                                }
                                                setPropertyValue={
                                                    setPropertyValue
                                                }
                                                setSelectedSpecAgentIdPt={
                                                    setSelectedSpecAgentIdPt
                                                }
                                                propertyValue={propertyValue}
                                                projectSettings={
                                                    projectSettings
                                                }
                                                isProjectPropertiesVisible={
                                                    showHideStates['properties']
                                                }
                                                isTrackChangesPaneVisible={
                                                    showHideStates['changes']
                                                }
                                                isOnlyAddedToProject={
                                                    onlyElementsAssignedToProjectFilter ||
                                                    selectedElementFilterType?.value ===
                                                        'Added to Work Results'
                                                }
                                                insertNewProductTypeElement={
                                                    insertNewProductTypeElement
                                                }
                                                contentTeamFromStudioPage={
                                                    contentTeamFromStudioPage
                                                }
                                                isDraftMode={isDraftMode}
                                                setIsDraftMode={setIsDraftMode}
                                                selectedSpecAgentIdPt={
                                                    selectedSpecAgentIdPt
                                                }
                                                updateCategoryElementState={
                                                    updateCategoryElementState
                                                }
                                                updateSelectedNuggetData={
                                                    setSelectedNuggetData
                                                }
                                                loadingBackdropProp={
                                                    loadingBackdrop
                                                }
                                                isElementsPaneDisplayed={
                                                    showLeftMostPane &&
                                                    currentPane === 'elements'
                                                }
                                                refetchSelectedProductsCount={
                                                    refetchSelectedProductsCount
                                                }
                                                isOMWorkspace={isOMWorkspace}
                                            />
                                        </RightMostPaneProvider>
                                    </GridItem>
                                </>
                            ) : (
                                <Backdrop
                                    data-testid="progressbar"
                                    className={classes.backdrop}
                                    open={isFromRedirect}
                                >
                                    <CircularProgress color="inherit" />
                                </Backdrop>
                            )}
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            ) : (
                <Backdrop
                    data-testid="progressbar"
                    className={classes.backdrop}
                    open={categoryLoading || isProcessingChunks}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
            )}
            {isProjectSettingsOpen &&
                (!isBetaWorkspaceToggleEnabled ? (
                    <ProjectSettingsDialog
                        isDialogDisplayed={isProjectSettingsOpen}
                        handleDialogClose={onProjectSettingsClicked}
                        settings={projectSettings}
                        saveSettings={handleSaveSettings}
                        firmDataPrivacySettings={
                            firmData?.firm?.dataPrivacySettings
                        }
                    />
                ) : (
                    <WorkspaceSettingsDialog
                        isDialogDisplayed={isProjectSettingsOpen}
                        handleDialogClose={onProjectSettingsClicked}
                        saveSettings={handleSaveSettings}
                        firmDataPrivacySettings={
                            firmData?.firm?.dataPrivacySettings
                        }
                    />
                ))}
            {addingBulkItemsInProgress && (
                <ProjectBulkAddProgressDialog
                    selectedElementCount={selectedBulkAddElements?.length ?? 0}
                    input={bulkAddInput}
                    onCompleted={handleBulkAddCompleted}
                    onError={handleBulkAddError}
                />
            )}

            <ConfirmationDialog
                dialogHeading="Quit Quick Add?"
                openDialog={cancelBulkItems}
                removeHeaderBorderForBulkAdd
                handleDialogClose={clearBulkAddSelection(false)}
                dialogActionCallback={clearBulkAddSelection(true)}
                contentNode={
                    <>
                        Continuing this action will quit the quick add process.
                        The items you have selected for quick adding will not be
                        added to your project.
                        <br />
                        Do you want to continue this action and quit quick
                        adding?
                    </>
                }
                removeFooterBorder={true}
                cancelText="Cancel"
                confirmText="Continue"
                size="small"
                confirmMessage=""
                dialogType="warning"
            />

            <ConfirmationDialog
                dialogHeading={
                    bulkAddSkippedElementPaths.length > 0
                        ? 'Add to Project'
                        : 'Add to Project - Success'
                }
                openDialog={addToProjectSuccess}
                removeHeaderBorderForBulkAdd
                dialogActionCallback={closeSuccessDialog}
                contentNode={
                    <BulkAddSuccessDialogContent
                        addedCount={addedBulkAddItemsCount}
                        skippedElementPaths={bulkAddSkippedElementPaths}
                    />
                }
                removeFooterBorder={true}
                confirmText="Close"
                size="small"
                cancelText=""
                confirmMessage=""
                dialogType="success"
            />
        </>
    )
}

const WorkspacePageContainer = () => {
    const [
        workspaceActionBarState,
        setWorkspaceActionBarState,
    ] = React.useState<Record<string, any>>({})
    const { projectId } = useContext(ProjectPageContext)

    const isContentTeamUser = useReactiveVar(isContentTeam)
    const isOMUser = useReactiveVar(isOmWorkspaceUser)
    const { currentUserActiveFirm } = React.useContext(AppBarContext)
    const contentTeamProject = useReactiveVar(contentTeamProjectId)
    const { currentUserInfo } = useReactiveVar(userInfo)

    // TODO: Remove this global state and use reactiveVar instead, remove the prop drills as well
    /**
     * @deprecated This global state is deprecated and will be removed in future versions.
     * Instead, use the reactive variables `isContentTeamFromStudioPage` and `setIsReactiveContentTeamFromStudioPage` from workspaceContents.ts.
     *
     * @type {boolean} contentTeamFromStudioPage - The current state value.
     * @type {Function} setContentTeamFromStudioPage - The function to update the state.
     */
    const [
        contentTeamFromStudioPage,
        setContentTeamFromStudioPage,
    ] = React.useState(false)
    const { setIsContentTeamFromStudioPage } = useWorkspace2Actions()

    const [isDraftMode, setIsDraftMode] = React.useState(false)

    const { kpUserId: userId, roles: userRoles } = currentUserInfo

    const savedStateValues = React.useMemo(() => {
        if (userId && projectId) {
            return (
                getFromLocalStorage(userId, projectId) ?? {
                    showHideStates: {
                        categories: true,
                        elements: true,
                        actionBar: false,
                        properties: true,
                    },
                }
            )
        }
    }, [userId, projectId])

    const updatePersistentStateValues = React.useCallback(
        (propKey: string, inputValue: boolean | any) => {
            saveToLocalStorage(propKey, inputValue, userId, projectId)
        },
        [userId, projectId]
    )
    React.useEffect(() => {
        // Wait for firm data before evaluating — otherwise the flag resets to false
        if (isOMUser && !currentUserActiveFirm?.bestPracticeProjectId) {
            return
        }

        if (isContentTeamUser) {
            if (
                !['DraftMasterSpecProject', 'DraftSpecTextProject'].includes(
                    projectId!
                )
            ) {
                setContentTeamFromStudioPage(false)
                setIsReactiveContentTeamFromStudioPage({ value: false })
                setIsContentTeamFromStudioPage(false)
            } else {
                setContentTeamFromStudioPage(true)
                setIsReactiveContentTeamFromStudioPage({ value: true })
                setIsContentTeamFromStudioPage(true)
            }
        }
        if (
            isOMUser &&
            projectId === currentUserActiveFirm?.bestPracticeProjectId
        ) {
            setContentTeamFromStudioPage(true)
            setIsReactiveContentTeamFromStudioPage({ value: true })
            setIsContentTeamFromStudioPage(true)
        }
    }, [
        projectId,
        isContentTeamUser,
        isOMUser,
        currentUserActiveFirm,
        contentTeamProject,
    ])

    React.useEffect(() => {
        updatePersistentStateValues(
            'showHideStates',
            savedStateValues?.showHideStates
        )

        setWorkspaceActionBarState({
            ...savedStateValues,
        })
    }, [savedStateValues, updatePersistentStateValues])

    const handleActionBarStateChange = React.useCallback(
        (stateKey: string, newValue: any, persist?: boolean) => {
            setWorkspaceActionBarState((prevWorkspaceActionBarState) => {
                const newState = setNestedObjectPropertyValue(
                    stateKey,
                    newValue,
                    prevWorkspaceActionBarState
                )

                return newState
            })
            if (persist) {
                updatePersistentStateValues(stateKey, newValue)
            }
        },
        [updatePersistentStateValues]
    )

    return (
        <WorkspaceActionBarProvider
            stateObject={workspaceActionBarState}
            setState={handleActionBarStateChange}
        >
            <NotificationProvider>
                <WorkspacePage
                    userId={userId ?? ''}
                    persistState={updatePersistentStateValues}
                    onStateChange={handleActionBarStateChange}
                    userRoles={userRoles}
                    contentTeamFromStudioPage={contentTeamFromStudioPage}
                    isDraftMode={isDraftMode}
                    setIsDraftMode={setIsDraftMode}
                />
            </NotificationProvider>
        </WorkspaceActionBarProvider>
    )
}

export const saveToLocalStorage = (
    propKey: string,
    inputValue: boolean | any,
    userId: string | undefined,
    projectId: string | undefined
) => {
    if (userId) {
        const saved = getLocalStorageValue(
            localStorageItemKey(projectId ?? ''),
            userId,
            '',
            localStorageKey(userId)
        )
        const pastItems = saved !== '' ? JSON.parse(saved as string) : {}
        updateKpMainLocalStore({
            currentUserId: userId,
            pageKey: localStorageKey(userId),
            itemKey: localStorageItemKey(projectId ?? ''),
            itemValue: JSON.stringify(
                setNestedObjectPropertyValue(propKey, inputValue, pastItems)
            ),
        })
    }
}

export default WorkspacePageContainer
